/*
 * Office panel module.
 *
 * - office panel works with 3 main kinds of data:
 *   - webphone instances and webphone SIP sessions
 *   - enhanced call data from deep backend
 *   - CDR records
 * - auxiliary data include:
 *   - speed dial directory data
 *   - contact lists
 *
 * Office panel is also a basis for agent panel, which is designed
 * to be an extension of the office panel. See apps/agent.js
 * Agent panel works with additional set of key data:
 *
 * - queue list
 *   - queue configuration (what prefix to use when calling out, category
 *     hierarchy associated with a queue, etc.)
 * - call detail information (data for call card, notes, etc.)
 * - outbound cases and campaigns
 * - callback cases
 *
 * Call identification
 * -------------------
 *
 * Bottom to top:
 *
 * Calls consist of SIP sessions. Each SIP session is identified by SIP
 * `Call-ID` header value. SIP session is implemented on the PBX end by
 * asterisk channel. SIP Call-ID is not readily accessible on asterisk
 * channel. Asterisk instead assigns each channel `unique_id` and `linked_id`.
 * `unique_id` is unique to the channel, and `linked_id` is shared by channels
 * that are somehow related. (eg. channel created by a call to PBX will
 * be related to channels created by PBX to route this call to connected
 * phones).
 *
 * SIP `Call-ID` header value is not readily available to AMI clients (eg. deep
 * backend), so it can't be used to pair SIP sessions to asterisk channels,
 * and thus to deep backend data.
 * 
 * To pair SIP session to call data from the deep backend we need to wait on the
 * client side for the information about `linked_id` that is associated with
 * the session to be delivered over a SIP transport inside an active SIP session.
 *
 * `linked_id` may be sent in INVITE request from the PBX or in final response
 * to INVITE request to a PBX in SIP header `P-Ast-LinkedId`. (if our
 * res_pjsip_linkedid module is loaded into asterisk) In addition, `linked_id`
 * can additionally be delivered via a NOTIFY message from the PBX.
 *
 * Toplevel unit of communication in the system is a call. A call may be
 * composed of multiple SIP sessions which may (or may not) have a different
 * `linked_id` each. To identify a call itself, the first `linked_id` seen in the
 * call is selected to become a `call_id` which is used for this purpose.
 *
 * There may be some significant delay between any of these events:
 *
 * - SIP session creation
 * - asterisk channel creation
 * - deep backend learning about a new asterisk channel
 * - deep backend delivering updated call information to all web clients
 * - web client learning of a call_id associated with the SPI session
 *
 * Web client primarily deals with SIP sessions (via JsSIP library). SIP
 * sessions provide the fastest feedback about the status of the part of
 * the call relevant to the web client. Especially when dialing out to PBX
 * it's only the web client's local SIP client that knows about the SIP
 * session at all and no information about the call may exist anywhere else,
 * yet.
 *
 * Any auxiliary data (call card information, outbound campaign case log, CDR,
 * ...) associated with a call are associated via the call's `call_id`. This ID
 * is only known by deep backend. For this ID to become known by the web client,
 * the web client has to wait until deep backend learns about the call and until
 * the call information from the deep backend start appearing in the output from
 * `GET /api/dialer/state`. Pairing of data from deep backend to SIP session
 * data in the web client happens via `linked_id`. So the web client has to
 * wait for two events: 1) `linked_id` being delivered to it via SIP transport,
 * 2) call information with this specific `linked_id` appearing on one of the
 * channels in call data from deep backend in GET /api/dialer/state.
 *
 * Only once the pairing succeeds can web client start performing tasks (or
 * allow the user to perform tasks) that require knowledge of `call_id`:
 *
 * - allow opening of the call card (or perform automatic opening)
 * - allow storing of auxiliary information about the call
 * - associate calls with outbound campaign cases
 * - etc.
 *
 * CDR records in the database are created after the call, so if any information
 * about the call has to be stored to database during the call, it has to be
 * stored under a call's `call_id` outside of CDR table. So any such storage
 * operation has to wait until the web client learns of the `call_id` from
 * the backend. Otherwise it would not be possible to pair CDR record to the
 * extra information stored to database before the end of a call.
 *
 * Basically, the call on the web client side can be in two states:
 *
 * - before call_id is known: only limited interaction with the call is possible
 *   (which is basically anything that can be performed over a SIP protocol
 *   itself, because we can't assume that the server even knows about the
 *   call, yet)
 *
 * - after call_id is known: full interaction with the call is possible
 *   (now it's possible to start using REST API to interact with the call)
 *
 * Queue information
 * -----------------
 *
 * Agent panel needs information about the queues the user has access to
 * in a curently activated/selected user group. This information includes:
 *
 * - list of available queues
 *   - queue names and numbers
 *   - type of queue (personal, agent)
 * - status of the user's active extension's membership in each queue
 *   - joined / not joined / paused
 *   - type of membership - dynamic/fixed
 * - status of queues themselves
 *   - how many agents are available in the queue
 *   - how many calls are waiting in the queue
 * 
 * Changes in any of this information must be propagated rapidly to all
 * web clients.
 *
 * This information is necessary in many UI components and contexts, so
 * it's kept uptodate and available via a global Office.QueueManager object.
 */

// Core data and HW control functions:

// {{{ Office.Headsets

GW.define('Office.HeadsetSetupDialog', 'Main.Dialog', {
	title: _('Headsety'),

	'after:initComponent'() {
		let me = this;

		this.bodyEl.gwCreateChild({
			ref: 'headsets',
			xtype: 'SmartTable',
			emptyText: _('Žádné headsety nejsou připojené'),
			getColumns() {
				return [{
					id: 'name',
					name: _('Název'),
				}, {
					id: 'ctl',
					name: _('Akce'),
					align: 'right',
					formatCell(td, v, row) {
						td.gwCreateChild({
							nodeName: 'button',
							className: 'error icon-only small',
							children: [Utils.useIcon('close')],
							'on:click': ev => me.removeHeadset(row),
						});
					},
				}];
			},
		}, this);

		this.bodyEl.gwCreateChild({
			className: 'headset-controls',
			children: [{
				nodeName: 'button',
				className: 'primary small icon-left',
				children: [Utils.useIcon('plus'), _('Připojit headset')],
				'on:click': () => this.addHeadset(),
			}, {
				nodeName: 'button',
				className: 'secondary small icon-left',
				children: [Utils.useIcon('bug'), _('Stáhnout debugovací report')],
				'on:click': async ev => {
					let data = await Office.Headsets.getDebugData();
					let file = new File([JSON.stringify(data, null, '    ')], "headsets.json", {
						type: "text/plain",
					});

					let url = URL.createObjectURL(file);
					let a = document.createElement('a');
					a.href = url;
					a.download = 'headsets.json';
					a.click();
					URL.revokeObjectURL(url);
				},
			}],
		}, this);

		this.refresh();
		this.mon(Office.Headsets, 'change', c => {
			this.refresh();
		});
	},

	async addHeadset() {
		await Office.Headsets.add();
	},

	async removeHeadset(h) {
		await Office.Headsets.remove(h.device);
	},

	async refresh() {
		let headsets = await Office.Headsets.get();

		this.headsets.setData(headsets.map(h => {
			return {
				name: h.productName,
				device: h,
			};
		}));
	},
});

/*
 * This object tracks the registered headset devices and synchronizes
 * their state (LEDs) with internal state set by setState() function.
 *
 * When the user presses a button on the headset (mute or hook switch)
 * an event is produced.
 */
GW.define('Office.Headsets', 'GW.Object', {
	singleton: true,
	logToConsole: true,

	initObject() {
		if (!this.isSupported()) {
			return;
		}

		this.__log = [];

		this.state = {
			offhook: false,
			mute: false,
			ring: false,
		};

		this.init();
	},

	log(...args) {
		args = args.map(a => {
			if (a instanceof HIDDevice) {
				return ['hiddev', (a.vendorId || 0).toString(16), (a.productId || 0).toString(16)].join(':');
			}

			if (a instanceof Uint8Array) {
				return [...a].map(v => String(v.toString(16)).padStart(2, '0')).join(':');
			}

			return a;
		});

		if (this.logToConsole) {
			console.log(...args);
		}

		this.__log.push(args);
		while (this.__log.length > 200) {
			this.__log.shift();
		}
	},

	isSupported() {
		return !!navigator.hid;
	},

	async add() {
		/*
		 * Request headset devices.
		 */
		let devices = await navigator.hid.requestDevice({
			filters: [{
				usagePage: 0x0b, // Telephony Device Page
				usage: 0x05, // Headset
			}, {
				usagePage: 0x0b, // Telephony Device Page
				usage: 0x01, // Phone
			}],
		});

		for (let d of devices) {
			this.watchDevice(d);
		}

		this.fire('change');
	},

	async remove(d) {
		if (d.opened) {
			await d.close();
		}

		await d.forget();

		this.fire('change');
	},

	async get() {
		return await navigator.hid.getDevices();
	},

	async setState(state) {
		this.state = state;

		this.log('set-headsets-state', state);

		await this.syncStateAll();
	},

	async syncStateAll() {
		let promises = [];
		let devices = await this.get();
		for (let device of devices) {
			if (device.opened) {
				promises.push(this.syncState(device));
			}
		}

		await Promise.allSettled(promises);
	},

	async syncState(device) {
		let ring = this.state.ring;
		let offhook = !ring && this.state.offhook;
		let mute = offhook && this.state.mute;

		/*
		 * Order of reports is important. Do not change it without
		 * retesting all headsets.
		 */
		await this.sendReports(device, [
			['off-hook', offhook ? 1 : 0],
			['mute', mute ? 1 : 0],
			['ring', ring ? 1 : 0],
		]);
	},

	async sendReports(device, items) {
		let usageMap = {
			"mute":     0x080009,
			"off-hook": 0x080017,
			"ring":     0x080018,
			"speaker":  0x08001e,
			"hold":     0x080020,
			"on-line":  0x08002a,
		};

		this.log(device, 'output-reports-request', items.map(i => i[0] + ':' + i[1]));

		/*
		 * Filter out unknown usages, map our usage names to their
		 * standard IDs.
		 */
		items = items.map(i => [usageMap[i[0]], i[1]]).filter(i => i[0]);

                let usageValueMap = new Map(items);
		if (usageValueMap.size == 0) {
			return;
		}

		let usageOrderMap = new Map(items.map((i, idx) => [i[0], idx]));
		let reports = [];

		/*
		 * Determine what reports to send base on provided usage items.
		 */
		this.findReportDesc(device, 'outputReports', reportDesc => {
			/*
			 * Check if we'll need to send this report.
			 */
			let send = false;
			let size = 0;
			for (let i of reportDesc.items) {
				size += i.reportSize * i.reportCount;

				for (let u of i.usages) {
					if (usageValueMap.has(u)) {
						send = true;
					}
				}
			}

			if (!send) {
				return;
			}

			/*
			 * Prepare the report.
			 */
			let report = new Uint8Array(Math.ceil(size / 8));
			let report_items = [];

			let bitpos = 0;
			for (let i of reportDesc.items) {
				let mask = (1 << i.reportSize) - 1;

				for (let ii = 0; ii < i.reportCount; ii++) {
					let usage = i.usages[ii];
					if (usage && usageValueMap.has(usage)) {
						let val = usageValueMap.get(usage);
						if (val === true) {
							val = 1;
						}

						if (Number.isInteger(val) && val) {
							report[Math.floor(bitpos / 8)] |= (val & mask) << (bitpos % 8);
						}
					}

					report_items.push([bitpos, usage, (report[Math.floor(bitpos / 8)] >> (bitpos % 8)) & mask]);

					bitpos += i.reportSize;
				}
			}

			reports.push([reportDesc.reportId, report, report_items]);
		});

		/*
		 * Sort reports based on the order of items passed from the user
		 * as best as we can.
		 */

		function findOrder(r) {
			for (let i of r[2]) {
				let order = usageOrderMap.get(i[1]);
				if (order != null) {
					return order;
				}
			}

			return 9999999;
		}

		reports.sort((a, b) => {
			return findOrder(a) - findOrder(b);
		});

		for (let r of reports) {
			/*
			 * Send the report
			 */
			this.log(device, 'output-report', r[0], r[1], r[2].map(i => {
				if (Number.isInteger(i[1])) {
					return [i[0], i[1].toString(16), i[2]].join(':');
				}
			}).filter(v => v));

			await device.sendReport(r[0], r[1]);
		}
	},

	/*
	 * - type: 'inputReports', 'outputReports', 'featureReports'
	 * - predFn(report, collection): true/false
	 */
	findReportDesc(device, type, predFn) {
		function search(c) {
			for (let r of c[type]) {
				if (predFn(r, c)) {
					return r;
				}

			}

			for (let ch of c.children) {
				let r = search(ch);
				if (r) {
					return r;
				}
			}
		}

		for (let c of device.collections) {
			let r = search(c);
			if (r) {
				return r;
			}
		}
	},

	onInput(device, usage, value) {
		this.log(device, 'raw-input', usage, value);

		if (usage == 'hook-switch') {
			if (!!this.state.offhook == !!value) {
				return;
			}
		} else if (!value) {
			return;
		}

		this.log(device, 'input', usage, value);

		this.fire('input', usage, value);
	},

	onInputReport(device, items, reportId, report, reportDesc) {
                this.log(device, 'input-report', items.map(i => {
			if (Number.isInteger(i[1])) {
				return i[0].toString(16) + ':' + i[1];
			}
		}), reportId, report, reportDesc);

		let usageMap = new Map([
			[0x0b0020, "hook-switch"],
			[0x0b0021, "flash"],
			[0x0b002f, "phone-mute"],
		]);

		for (let [usage, value] of items) {
			let usageName = usageMap.get(usage);
			if (usageName) {
				/*
				 * Found a usage we understand,
				 * report it 
				 */
				this.onInput(device, usageName, value);
			}
		}
	},

	handleInputReport(device, reportId, report) {
		let reportDesc = this.findReportDesc(device, 'inputReports', r => r.reportId == reportId);
		if (!reportDesc) {
			return;
		}

		let off = 0;
                let parsedItems = [];

		for (let i of reportDesc.items) {
			let sz = i.reportSize * i.reportCount;

			if (Math.floor((off + sz) / 8) == Math.floor(off / 8) && sz < 8) {
				/*XXX: we only support report items that fit in a byte */
				let val = report[Math.floor(off / 8)];

				val = val >> off % 8;
				val = val & ((1 << sz) - 1);

				for (let n = 0; n < i.reportCount; n++) {
					let sv = (val >> n * i.reportSize) & ((1 << i.reportSize) - 1);

					if (!i.usages[n]) {
						continue;
					}

					parsedItems.push([i.usages[n], sv]);
				}
			}

			off += sz;
		}

		this.onInputReport(device, parsedItems, reportId, report, reportDesc);
	},

	async watchDevice(device) {
		try {
			await device.open();

			device.addEventListener("inputreport", ev => {
				let report = new Uint8Array(ev.data.buffer);

				// log reports for debug log
				device.__reports = device.__reports || [];
				device.__reports.push({id: ev.reportId, data: Array.from(report)});
				while (device.__reports.length > 200) {
					device.__reports.shift();
				}

				this.handleInputReport(device, ev.reportId, report);
			});

			await this.syncState(device);
		} catch (ex) {
			console.log(ex);
		}
	},

	async init() {
		navigator.hid.addEventListener("connect", ev => {
			this.watchDevice(ev.device);

			this.fire('change');
		});

		navigator.hid.addEventListener("disconnect", ev => {
			this.fire('change');
		});

		let devices = await navigator.hid.getDevices();
		for (let d of devices) {
			this.watchDevice(d);
		}

		this.fire('change');
	},

	async getDebugData() {
		let data = {
			devices: [],
			log: this.__log,
		};

		let devices = await this.get();
		for (let device of devices) {
			if (device.opened) {
				data.devices.push({
					vendorId: device.vendorId,
					productId: device.productId,
					productName: device.productName,
					collections: device.collections,
					last_reports: device.__reports,
				});
			}
		}

		return data;
	},

	runTester() {
		new Main.Dialog({
			title: 'Headset tester',

			'after:initComponent'() {
				this.mon(Office.Headsets, 'input', (h, usage, value) => {
					if (usage == 'phone-mute') {
						h.setState({
							...h.state,
							mute: !h.state.mute,
						});
					}

					if (usage == 'hook-switch') {
						h.setState({
							...h.state,
							offhook: value,
						});
					}

					this.bodyEl.gwCreateChild({
						className: 'event',
						textContent: usage + ' ' + value,
					});
				});

				this.bodyEl.gwCreateChild({
					className: 'buttons',
					children: [{
						nodeName: 'button',
						className: 'primary',
						textContent: 'mute',
						'on:click': () => Office.Headsets.setState({
							...Office.Headsets.state,
							mute: !Office.Headsets.state.mute,
						}),
					}, {
						nodeName: 'button',
						className: 'primary',
						textContent: 'hook',
						'on:click': () => Office.Headsets.setState({
							...Office.Headsets.state,
							offhook: !Office.Headsets.state.offhook,
						}),
					}, {
						nodeName: 'button',
						className: 'primary',
						textContent: 'ring',
						'on:click': () => Office.Headsets.setState({
							...Office.Headsets.state,
							ring: !Office.Headsets.state.ring,
						}),
					}],
				});
			},
		});
	},
});

// }}}
// {{{ Office.RingerDeviceSetupDialog

GW.define('Office.RingerDeviceSetupDialog', 'Main.Dialog', {
	title: _('Nastavení vyzváněcího zařízení'),

	'after:initComponent'() {
		let me = this;

		this.bodyEl.gwCreateChild({
			ref: 'outputs',
			xtype: 'SmartTable',
			emptyText: _('Žádné audio výstupy nebyly nalezeny'),
			getColumns() {
				return [{
					id: 'name',
					name: _('Název'),
				}, {
					id: 'ctl',
					name: _('Akce'),
					align: 'right',
					formatCell(td, v, row) {
						if (row.selected) {
							td.gwCreateChild({
								nodeName: 'button',
								className: 'success icon-only small',
								children: [Utils.useIcon('check')],
								'on:click': ev => ev.preventDefault(),
							});
						} else {
							td.gwCreateChild({
								nodeName: 'button',
								className: 'secondary icon-only small',
								children: [Utils.useIcon('check')],
								'on:click': ev => me.selectOutput(row.sinkId),
							});
						}
					},
				}];
			},
		}, this);

		this.bodyEl.gwCreateChild({
			className: 'ringer-controls',
			children: [{
				nodeName: 'button',
				className: 'secondary small icon-left',
				children: [Utils.useIcon('cross'), _('Vrátit výchozí nastavení')],
				'on:click': ev => this.cancelSetup(),
			}],
		}, this);

		this.refresh();
	},

	async selectOutput(sinkId) {
		let result = await navigator.permissions.query({name: 'microphone'});
		if (result.state == 'granted') {
			localStorage.setItem('office-webphone-ringer-sink-id', sinkId);

			this.refresh();
		} else {
			Application.notify({
				kind: 'error',
				text: _('Nepovolili jste změnu audio výstupu. Nelze změnit zařízení pro vyzvánění.'),
				priority: 5,
				timeout: 5000,
			});
		}
	},

	cancelSetup() {
		localStorage.removeItem('office-webphone-ringer-sink-id');

		this.refresh();
	},

	async refresh() {
		let badPermsMsg = _('Zakázali jste změnu audio výstupu v prohlížeči. Nelze změnit zařízení pro vyzvánění.');

		let result = await navigator.permissions.query({name: 'microphone'});
		if (result.state == 'granted') {
			/* fall through */
		} else if (result.state === 'prompt') {
			try {
				await navigator.mediaDevices.getUserMedia({audio: true});
			} catch(ex) {
				this.outputs.msgEl.textContent = badPermsMsg;
				this.outputs.setData([]);
				return;
			}
		} else if (result.state === 'denied') {
			this.outputs.msgEl.textContent = badPermsMsg;
			this.outputs.setData([]);
			return;
		}

		this.outputs.msgEl.textContent = this.outputs.emptyText;

		let devices = await navigator.mediaDevices.enumerateDevices();
		let outputs = devices.filter(d => d.kind == 'audiooutput');
		let selected = localStorage.getItem('office-webphone-ringer-sink-id') || 'default';

		this.outputs.setData(outputs.map(d => {
			return {
				name: d.label,
				sinkId: d.deviceId,
				selected: selected == d.deviceId,
			};
		}));
	},
});

// }}}
// {{{ Office.WebPhone

/*
 * JsSIP expects mediaDevices to be defined and to have getUserMedia() method,
 * otherwise it swallows some errors related to browser security mechanisms that
 * remove access to media.
 *
 * If the browser lacks navigator.mediaDevices, we provide our own, here.
 */
navigator.mediaDevices = navigator.mediaDevices || {
	async getUserMedia() {
		throw new Error(_('Nelze získat přístup k mediálním zařízením. Aplikace neběží na https, nebo je přístup vypnutý ve vašem prohlížeči.'));
	},
};

/*
 * Thin wrapper around JsSIP UA.
 *
 * - logs UA events, etc.
 * - manages sessions
 * - manages registrations
 */
GW.define('Office.WebPhone', 'GW.Object', {
	initObject() {
		Office.WebPhone.next_phone_id = (Office.WebPhone.next_phone_id || 0) + 1;

		this.id = Office.WebPhone.next_phone_id;
		this.sessions = new Set;
	},

	'before:destroy'() {
		this.disconnect();
	},

	log(o) {
		o.webphone = this.id;

		console.log('log', o);
		this.fire('log', o);

		if (o.session) {
			Performance.pushSessionUpdate(o);
		}
	},

	connect() {
		let me = this;

		this.disconnect();

		function log(msg, extra) {
			me.log({
				msg,
				...extra,
			});
		}

		let s = Application.session;
		if (!s || !s.pbx_websocket_url || !s.webrtc_sip_username) {
			this.registration_failure_reason = 'nocreds';
			this.registration_failure_reason_message = _('Server neposlal přihlašovací údaje k lince');
			this.fire('registrationchange');
			return;
		}

		let ws_url = s.pbx_websocket_url;
		if (ws_url.startsWith('/')) {
			ws_url = ((location.protocol == 'https:') ? 'wss' : 'ws') + '://' + location.host + ws_url;
		}

		/*
		 * We need to get rid of UA without calling stop(), so that
		 * we can register elsewhere immediately. We achieve that by
		 * doing manual re-registration and by simply stopping it when UA
		 * is no longer needed.
		 *
		 * this.socket/ua references can only be used in the disconnect
		 * function.
		 */
		this.ice_servers = s.webrtc_ice_servers || [];
		this.socket = new JsSIP.WebSocketInterface(ws_url);
		this.ua = new JsSIP.UA({
			sockets: [this.socket],
			uri: 'sip:' + s.webrtc_sip_endpoint + '@' + s.webrtc_sip_host,
			authorization_user: s.webrtc_sip_username,
			password: s.webrtc_sip_password,
			user_agent: 'JsSIP',
		});

		this.extension = s.user.extension;

		this.ua.start();

		this.ua.on('connecting', e => {
			log('sip connecting');
		});

		this.ua.on('connected', e => {
			log('sip connected');
		});

		this.ua.on('disconnected', e => {
			log('sip disconnected');
		});

		this.ua.on('registered', e => {
			log('sip registered', {data: e.response.data});

			this.registration_failure_reason = null;
			this.registration_failure_reason_message = null;

			this.fire('registrationchange');
		});

		this.ua.on('unregistered', e => {
			log('sip unregistered');

			this.registration_failure_reason = null;
			this.registration_failure_reason_message = null;

			this.fire('registrationchange');
		});

		this.ua.on('registrationFailed', e => {
			log('sip registration failed', {response: e.response});

			let status = e.response && e.response.status_code;
			if (status == 403) {
				this.registration_failure_reason = 'full';
				this.registration_failure_reason_message = _('Překročen limit počtu WebRTC zařízení. Použijte pro volání office panel, který máte právě otevřený v jiné záložce, či prohlížeči.');
			} else {
				this.registration_failure_reason = 'other';
				this.registration_failure_reason_message = this.cause_map.get(e.cause) || e.cause;
			}

			this.fire('registrationchange');
		});

		this.ua.on('sipEvent', e => {
			log('sip event');
		});

		this.ua.on('newRTCSession', e => {
			log('sip new session', {
				session: e.session._id,
				originator: e.originator,
				request: e.request.data,
				remote_identity: {
					name: e.session.remote_identity.display_name,
					uri: e.session.remote_identity.uri.toString(),
				},
			});

			this.onNewSession(e.session, e.request);
		});
	},

	disconnect() {
		if (this.socket) {
			this.ua.stop();

			this.registration_failure_reason = null;
			this.registration_failure_reason_message = null;

			delete this.ua;
			delete this.socket;
			delete this.extension;
		}
	},

	cause_map: new Map(Object.entries({
		ADDRESS_INCOMPLETE: _('Chybné číslo'),
		AUTHENTICATION_ERROR: _('Přihlášení selhalo'),
		BAD_MEDIA_DESCRIPTION: _('Selhalo navázání datového spojení'),
		BUSY: _('Obsazeno'),
		BYE: _('Hovor ukončen'),
		CANCELED: _('Hovor odmítnut'),
		CONNECTION_ERROR: _('Sehhalo SIP spojení k ústředně'),
		DIALOG_ERROR: _('Selhalo SIP spojení k ústředně (chyba dialogu)'),
		EXPIRES: _('Hovor vypršel, protože nebyl vyzvednut včas'),
		INCOMPATIBLE_SDP: _('Selhalo navázání datového spojení, nekompatibilní kodek'),
		INTERNAL_ERROR: _('Neznámá chyba'),
		MISSING_SDP: _('SDP zpráva je prázdná'),
		NOT_FOUND: _('Volané číslo neexistuje'),
		NO_ACK: _('Selhalo navázání datového spojení, protistrana nepotvrdila vyzvednutí hovoru'),
		NO_ANSWER: _('Volaný neodpověděl'),
		REDIRECTED: _('Hovor přesměrován'),
		REJECTED: _('Hovor odmítnut'),
		REQUEST_TIMEOUT: _('Selhalo SIP spojení, protistrana nodpověděla'),
		RTP_TIMEOUT: _('Datové spojení bylo přerušeno'),
		SIP_FAILURE_CODE: _('Neznámá chyba SIP'),
		UNAVAILABLE: _('Volaný není dostupný'),
		USER_DENIED_MEDIA_ACCESS: _('Prohlížeč zablokoval přístup k audio zařízení'),
		WEBRTC_ERROR: _('Neznámá chyba WebRTC'),
	}).map(e => {
		return [JsSIP.C.causes[e[0]], e[1]];
	})),

	showErrorMessage(data) {
		let reason, sip_status;
		if (data.message) {
			let headers = data.message.headers || {};
			for (let [k, v] of Object.entries(headers)) {
				if (k.toLowerCase() == 'reason') {
					reason = (v[0] || {}).raw;
				}
			}

			sip_status = data.message.status_code;
		}

		let text = this.cause_map.get(data.cause) || data.cause;

		// don't show error when the other side stops dialing us (this
		// is common when picking up the incoming call on HW phone
		// instead of in webphone)
		if (data.cause == JsSIP.C.causes.CANCELED) {
			text = null;
		}

		if (JsSIP.C.causes.SIP_FAILURE_CODE == data.cause) {
			if (sip_status == 503) {
				text = _('Volaný není dostupný');
			}

			if (reason == 'Q.850;cause=34') {
				text = _('Volaný není dostupný');
			}
		}

		if (text) {
			Application.notify({
				kind: 'error',
				exclusive: 'sip-error',
				text,
				priority: 5,
				timeout: 3000,
			});
		}
	},

	isRingingMuted() {
		return this.ringingMuted;
	},

	/*
	 * When ringing is muted, we must mute all __ringer elements for sessions
	 * in the ringing state. We must also prevent ringing on new sessions
	 * by muting the __ringer element by default.
	 */
	muteRinging(muted) {
		this.ringingMuted = !!muted;

		for (let s of this.sessions) {
			s.__ringer.muted = this.ringingMuted;
		}
	},

	getRegistrationStatus() {
		if (!this.ua) {
			return {
				registered: false,
			};
		}

		return {
			registered: this.ua.isRegistered(),
			cause: this.registration_failure_reason,
			cause_message: this.registration_failure_reason_message,
		};
	},

	createAudio() {
		return document.body.gwCreateChild({
			nodeName: 'audio',
			autoplay: 1,
		});
	},

	allowSession(session, initial_request) {
		return true;
	},

	onNewSession(session, initial_request) {
		let me = this;

		if (!this.allowSession(session, initial_request)) {
			log('session rejected');
			return;
		}

		let audio = this.createAudio();
		let ringer = this.createAudio();
		let warnIceTimer;

		session.__audio = audio;
		session.__ringer = ringer;
		session.__webphone = this;
		session.__early_ts = new Date;

		this.sessions.add(session);

		function log(msg, extra) {
			me.log({
				msg,
				session: session._id,
				...extra,
			});
		}

		function receive_linked_id(linked_id) {
			if (session.__linked_id != linked_id) {
				log('session linked_id', {linked_id});
				session.__linked_id = linked_id;

				me.fire('sessionchange');
				me.fire('gotlinkedid', session);
			}
		}

		let linkedid_hdr = initial_request.headers['P-Ast-Linkedid'] &&
			initial_request.headers['P-Ast-Linkedid'][0];
		if (linkedid_hdr) {
			receive_linked_id(linkedid_hdr.raw);
		}

		/*
		 * We need to override internal function of JsSIP.RTCSession to
		 * be able to receive linkedids as part of a session dialog.
		 *
		 * Linked id of the channel is sent by the backend via a special
		 * NOTIFY message after the channel is created.
		 */
		session.receiveRequest = function(request) {
			let is_linked_id_notify = request.method == JsSIP.C.NOTIFY &&
				request.event && request.event.event == 'bcfe-linkedid' &&
				request.headers['X-Linked-Id'] && request.headers['X-Linked-Id'][0];

			if (is_linked_id_notify) {
				let linked_id = request.headers['X-Linked-Id'][0].raw;

				receive_linked_id(linked_id);
				request.reply(200);
				return;
			}

			/*
			 * Linked id can be also updated in an ongoing call (for example 
			 * if dialing into parked space to retrieve a parked call). 
			 * UPDATE message with updated linkedid is received in that case
			 */
			if (request.method == JsSIP.C.UPDATE &&
				request.headers['P-Ast-Linkedid'] && request.headers['P-Ast-Linkedid'][0]
			) {
				receive_linked_id(request.headers['P-Ast-Linkedid'][0].raw);
			}

			/*
			 * Let the JsSIP to handle everything else.
			 */
			session.constructor.prototype.receiveRequest.call(this, request);
		};

		session._receiveInviteResponse = function(response) {
			let linkedid_hdr = response.headers['P-Ast-Linkedid'] &&
				response.headers['P-Ast-Linkedid'][0];
			if (linkedid_hdr) {
				receive_linked_id(linkedid_hdr.raw);
			}

			session.constructor.prototype._receiveInviteResponse.call(this, response);
		};

		session.answer = function() {
			/*
			 * Ignore answer request if the call is already
			 * established.
			 */
			if (session.isEstablished()) {
				return;
			}

			session.constructor.prototype.answer.call(this, {
				extraHeaders: ['P-BCFE-TS: ' + (new Date).getTime()],
				mediaConstraints: {
					audio: true,
					video: false,
				},
				pcConfig: {
					iceServers: me.ice_servers,
				},
			});
		};

		function stop_audio() {
			log('session stop audio');

			audio.srcObject = null;
			audio.pause();

			ringer.srcObject = null;
			ringer.pause();

			session.__got_call_audio = false;
		}

		function stop_ring() {
			if (!session.__got_call_audio) {
				stop_audio();
			}
		}

		function start_ring() {
			if (!session.__got_call_audio) {
				log('session start ring');

				ringer.src = '/audio/ring1.mp3';
				ringer.loop = true;
				ringer.muted = me.ringingMuted;

				if (ringer.setSinkId) {
					ringer.setSinkId(localStorage.getItem('office-webphone-ringer-sink-id') || 'default').catch(console.error);
				}

				session.__got_call_audio = false;
			}
		}

		function play_stream(stream) {
			log('session play stream');

			session.__got_call_audio = true;

			ringer.srcObject = null;
			ringer.pause();

			audio.loop = false;
			audio.srcObject = stream;
			audio.muted = false;
		}

		function release_session() {
			stop_statistics();

			stop_audio();
			me.sessions.delete(session);
			session.__audio.remove();
			session.__ringer.remove();

			clearTimeout(warnIceTimer);
			warnIceTimer = null;
		}

		function update_statistics() {
			if (!session.connection) {
				return;
			}

			session.connection.getStats(null).then(stats => {
				session.__last_stats = [...stats.values()];

				if (session.__linked_id) {
					Performance.pushRTCStats({
						linked_id: session.__linked_id,
						stats: session.__last_stats,
					});
				}
			}, err => null);
		}

		function start_statistics() {
			if (!session.__stats_timer) {
				session.__stats_timer = setInterval(update_statistics, 1000);
			}
		}

		function stop_statistics() {
			clearInterval(session.__stats_timer);
		}

		session.on('confirmed', () => {
			log('session confirmed');

			stop_ring();
			session.__start_ts = new Date;

			this.fire('sessionchange');
		});

		session.on('accepted', () => {
			log('session accepted');

			stop_ring();
			session.__start_ts = new Date;

			this.fire('sessionchange');
		});

		session.on('failed', (data) => {
			log('session failed', {
				reason: data.message ? data.message.data : null,
			});

			release_session();

			this.fire('sessionchange');

			try {
				this.showErrorMessage(data);
			} catch(ex) { }
		});

		session.on('progress', (data) => {
			log('session progress');

			start_ring();

			this.fire('sessionchange');
		});

		session.on('ended', e => {
			log('session ended');

			release_session();

			this.fire('sessionchange');
		});

		session.on('peerconnection', e => {
			log('session peer connection');

			setup_connection();
		});

		session.on('getusermediafailed', e => {
			Application.notify({
				exclusive: 'media-error',
				kind: 'error',
				text: e.message,
				priority: 5,
				timeout: 5000,
			});

			log('getusermediafailed', {error: e.message});
		});

		session.on('peerconnection:createofferfailed', e => {
			log('createofferfailed', {error: e.message});
		});

		session.on('peerconnection:createanswerfailed', e => {
			log('createanswerfailed', {error: e.message});
		});

		session.on('peerconnection:setlocaldescriptionfailed', e => {
			log('setlocaldescriptionfailed', {error: e.message});
		});

		session.on('peerconnection:setremotedescriptionfailed', e => {
			log('setremotedescriptionfailed', {error: e.message});
		});

		session.on('muted', e => {
			log('muted');
			this.fire('sessionchange');
		});

		session.on('unmuted', e => {
			log('unmuted');
			this.fire('sessionchange');
		});

		session.on('hold', e => {
			log('hold');
			this.fire('sessionchange');
		});

		session.on('unhold', e => {
			log('unhold');
			this.fire('sessionchange');
		});

		if (session.connection) {
			setup_connection();
		}

		function setup_connection() {
			let c = session.connection;

			start_statistics();

			c.ontrack = evt => {
				play_stream(evt.streams[0] || evt.stream);
			};

			c.addEventListener('icegatheringstatechange', ev => {
				if (c.iceGatheringState == 'complete') {
					session.connection.getStats(null).then(stats => {
						log('ice candidates', {
							stats: [...stats.values()],
						});
					}, err => null);
				}
			});

			c.addEventListener("connectionstatechange", ev => {
				let state = c.connectionState;

				log('rtc', {
					state,
				});
			});

			c.addEventListener('iceconnectionstatechange', ev => {
				let state = c.iceConnectionState;

				clearTimeout(warnIceTimer);
				warnIceTimer = null;
				if (state == 'checking') {
					warnIceTimer = setTimeout(warnICE, 5000);
				}

				c.getStats(null).then(stats => {
					log('ice agent', {
						state,
						stats: [...stats.values()],
					});
				}, err => null);
			});

			c.addEventListener('signalingstatechange', ev => {
				let state = c.signalingState;
				log('signaling', {
					state,
					local: c.currentLocalDescription && c.currentLocalDescription.toJSON(),
					remote: c.currentRemoteDescription && c.currentRemoteDescription.toJSON(),
				});
			});

			c.addEventListener('icecandidateerror', ev => {
				let {address, errorCode, errorText, port, url} = ev;

				log('ice candidate error', {
					address, errorCode, errorText, port, url,
				});
			});
		}

		function warnICE() {
			warnIceTimer = null;

			session.connection.getStats(null).then(stats => {
				log('ice agent', {
					state: 'ice slow',
					stats: [...stats.values()],
				});
			}, err => null);

			if (Office.IceWarned) {
				return;
			}

			Office.IceWarned = true;

			Application.notify({
				kind: 'warning',
				text: _('Nastavení sítě či ústředny není optimální. Sestavení zvukového spojení hovoru může trvat delší dobu či selhávat.'),
				timeout: 7000,
			});
		}

		this.fire('sessionchange');
	},

	getSessionStatus(session) {
		let status = 'none';
		if (session.isEnded()) {
			status = 'none';
		} else if (session.isInProgress()) {
			status = session.direction === 'incoming' ? 'ringing' : 'calling';
		} else if (session.isEstablished()) {
			status = 'calling';
		}

		return {
			// ringing, calling
			status,

			originator: session.originator,

			peer_name: session.remote_identity.display_name,
			peer_uri: session.remote_identity.uri.toString(),
			peer_user: session.remote_identity.user,
		};
	},

	getSessions() {
		return [...this.sessions].map(s => {
			return this.getSessionStatus(s);
		});
	},

	getSession(linked_id) {
		for (let s of this.sessions) {
			if (s.__linked_id && s.__linked_id == linked_id) {
				return s;
			}
		}
	},

	hasSessionWithLinkedid(linked_id) {
		return !!this.getSession(linked_id);
	},

	call(number) {
		this.log({
			msg: 'ua call',
			number,
		});

		return this.ua.call('sip:' + number + '@' + Application.session.webrtc_sip_host, {
			extraHeaders: ['P-BCFE-TS: ' + (new Date).getTime()],
			mediaConstraints: {
				audio: true,
				video: false,
			},
			pcConfig: {
				iceServers: this.ice_servers,
			},
		});
	},
});

// }}}
// {{{ Office.CallManager

GW.define('Office.CallManager', 'GW.Object', {
	singleton: true,
	active_device: localStorage.getItem('dialer-device') || 'webrtc',

	initObject() {
		/*
		 * Set of additionally monitored linked_ids in this call
		 * manager. (In addition to all calls related to current
		 * users's active extension)
		 */
		this.extra_linked_ids = new Map;
		/*
		 * Webphones that were removed after extension switch, and
		 * still have active calls in them.
		 */
		this.extra_webphones = new Set;

		this.initHeadsets();
	},

	/*
	 * Check if it's possible to switch devices in a dialer at this time.
	 *
	 * It's only possible when no call is connected, ie.:
	 * - no calls at all
	 * - before picking up incomming call (so user can choose how to pick it up)
	 */
	canChangeDevice() {
		let call = this.getState().active_call;

		return !call || (call.status == 'incomming');
	},

	/*
	 * Change selected device in the dialer.
	 */
	setActiveDevice(device) {
		if (!this.canChangeDevice()) {
			return;
		}

		localStorage.setItem('dialer-device', device.id);
		this.active_device = device.id;

		this.updateState();
	},

	/*
	 * Function to check whether the user can perform a particular action
	 * with the current state of the dialer.
	 *
	 * This check depends on the state of the dialer and selected device:
	 *
	 * - user can start a new call if the selected device is controllable
	 *   and no other call is ongoing,
	 * - etc.
	 *
	 * This function assumes at most one session being possible.
	 */
	canDo(action) {
		if (!Application.socket.isReady() || !this.active) {
			return false;
		}

		let state = this.getState();
		let has_exten = this.exten != null;

		if (!state.selected_device || state.selected_device.disabled) {
			return false;
		}

		/*
		 * Check if active call is handled by this window's webphone
		 * session. If not, we can't control it from here.
		 */
		let is_webrtc = state.selected_device.id == 'webrtc';
		let active_call = state.active_call;
		if (is_webrtc && active_call && !active_call.webphone_session) {
			return false;
		}

		/*
		 * Check if action is supported by the selected device at all.
		 */
		let local_webrtc_actions = ['dial', 'hangup', 'answer', 'mute', 'unmute', 'hold', 'unhold', 'dtmf'];
		let is_supported = state.selected_device.device.supported_actions.includes(action)
			|| (is_webrtc ? local_webrtc_actions.includes(action) : false);
		if (!is_supported) {
			return false;
		}

		/*
		 * Perform call status specific decisions.
		 */
		switch (action) {
		case 'dial':
			return !active_call && has_exten;
		case 'answer':
			return !!active_call && active_call.status == 'incomming';
		case 'hangup':
			return !!active_call && (is_webrtc || !!active_call.call_id);
		case 'attended-transfer':
		case 'blind-transfer':
			return !!active_call && active_call.call_id && active_call.status == 'connected';
		case 'cancel-transfer':
		case 'complete-transfer':
		case 'park':
			// an active call with known call_id
			return !!active_call && active_call.call_id;
		case 'mute':
		case 'unmute':
		case 'hold':
		case 'dtmf':
			return !!active_call && active_call.status == 'connected';
		case 'unhold':
			return !!active_call && active_call.status == 'hold';
		default:
			return false;
		}
	},

	/*
	 * Function to trigger a given action, if canDo returns true for it.
	 *
	 * Action is performed either locally via SIP webphone, or remotely
	 * via business backend, depending on currently selected device,
	 * and currently active call.
	 */
	async doAction({action, number, send_metadata, data}) {
		let state = this.getState();
		let numberActions = new Set([
			'attended-transfer',
			'blind-transfer',
			'dial',
		]);

		if (!state.selected_device) {
			throw new Error(_('Není dostupné žádné zařízení.'));
		}

		if (numberActions.has(action)) {
			number = String(number || '').trim().replace(/\s+/, '');
			if (!number) {
				Application.notify({
					exclusive: 'dialer-feedback',
					kind: 'error',
					text: _('Číslo musí být vyplněno'),
					priority: 5,
					timeout: 3000,
				});
				return;
			}

			if (!number.match(/^\+?[0-9*+#]{1,20}/)) {
				Application.notify({
					exclusive: 'dialer-feedback',
					kind: 'error',
					text: _('Chybný formát čísla'),
					priority: 5,
					timeout: 3000,
				});
				return;
			}
		}

		Performance.pushEvent({
			kind: 'dialer action',
			action,
			number,
			actve_call: state.active_call ? state.active_call.call_id : null,
		});

		if (state.selected_device.id == 'webrtc') {
			let getSession = () => {
				let session = state.active_call.webphone_session;
				if (!session) {
					throw new Error(_('Hovor neprobíhá v tomto okně prohlížeče.'));
				}

				return session;
			};

			// use webrtc directly
			switch (action) {
			case 'dial': {
				let session = this.webphone.call(number);
				session.__send_metadata = send_metadata;
				return;
			}
			case 'hangup':
				getSession().terminate();
				return;
			case 'answer':
				getSession().answer();
				return;
			case 'mute':
				getSession().mute();
				return;
			case 'unmute':
				getSession().unmute();
				return;
			case 'hold':
				getSession().hold();
				return;
			case 'unhold':
				getSession().unhold();
				return;
			case 'dtmf':
				getSession().sendDTMF(number);
				return;
			}
			// pass all other events to BE (eg. 'blind-transfer')
		}

		let extra_linked_ids = [...this.extra_linked_ids.keys()];

		await REST.POST('dialer/action', {
			extra_linked_ids,
			action,
			number,
			call_id: state.active_call ? state.active_call.call_id : null,
			call_exten: state.active_call ? state.active_call.my_exten : null,
			metadata: send_metadata,
			...(data || {}),
		});

		let text;
		if (action == 'blind-transfer') {
			text = _('Hovor je přesměrováván, prosím vyčkejte...');
		} else if (action == 'attended-transfer') {
			text = _('Hovor je přesměrováván, prosím vyčkejte...');
		} else if (action == 'cancel-transfer') {
			text = _('Přesměrování se ukončuje, prosím vyčkejte...');
		}

		if (text) {
			Application.notify({
				kind: 'success',
				exclusive: 'dialer-feedback',
				text,
				priority: 5,
				timeout: 3000,
			});
		}
	},

	/*
	 * This function puts active calls (if any) on hold, and removes hold from
	 * a passed in call.
	 */
	returnFromHold(call) {
		let state = this.getState();
		if (state.active_call && state.active_call.webphone_session && !state.active_call.webphone_session.isOnHold().local) {
			state.active_call.webphone_session.hold();
		}

		if (call.webphone_session && call.webphone_session.isOnHold().local) {
			call.webphone_session.unhold();
		}
	},

	/*
	 * This function puts active calls (if any) into a parking space, 
	 * and unpark the passed call
	 */
	async unpark(call) {
		let active_call = this.getState().active_call;
		if (active_call) {
			/*
			 * Ideally the active call could be switched with the parked call via Redirect AMI
			 * event. This would however mess up deepBC connections with changing linkedId of
			 * the active channel and break association of webphone SIP session with BE call_id.
			 * Until this will be solved, we are using simpler method - just park the active call and
			 * unpark the parked call even thought this method does have several cons:
			 *  - there can be delay between parking the active call and unparking the parked call, 
			 *    which can be used by Asterisk to antoher incoming call (either from queue or other).
			 *    This can be partly solved by user by manually pausing the agent in queues and 
			 *    unpausing after unparking the parked call.
			 *  - Unparking a call is done by dialing the parking_space, so there will be a delay 
			 *    (cca half a second) between dialing parking_space exten and showing connected to
			 *    the call which is  visible in UI as dialing into the parking_space exten. 
			 */
			await this.doAction({
				action: 'park'
			});
		}
		if (!call.parking_space) {
			console.warn("Could not unpark call, parking_space is not set");
			return;
		}
		await this.doAction({
			action: 'dial',
			number: call.parking_space
		});
	},

	/*
	 * Show a popup that allows the user to select from the list of
	 * phone numbers.
	 */
	selectContact(ev, peers) {
		return new Promise((res, rej) => {
			if (peers.length > 1) {
				let popup = new PopupMenu({
					options: peers.map(peer => {
						return {
							text: [peer.number, peer.name, peer.company].filter(v => v).join(', '),
							handle() {
								res(peer);
							},
						};
					}),
				});

				PopupManager.show(popup, {
					anchorEl: ev.target,
					forceAxis: 'vertical',
					preferReadingOrder: true,
				});
			} else {
				peers[0] && res(peers[0]);
			}
		});
	},

	/*
	 * This function puts active calls (if any) into a parking space, 
	 * and unpark the passed call
	 */
	async unpark(call) {
		let active_call = this.getState().active_call;
		if (active_call) {
			/*
			 * Ideally the active call could be switched with the parked call via Redirect AMI
			 * event. This would however mess up deepBC connections with changing linkedId of
			 * the active channel and break association of webphone SIP session with BE call_id.
			 * Until this will be solved, we are using simpler method - just park the active call and
			 * unpark the parked call even thought this method does have several cons:
			 *  - there can be delay between parking the active call and unparking the parked call, 
			 *    which can be used by Asterisk to antoher incoming call (either from queue or other).
			 *    This can be partly solved by user by manually pausing the agent in queues and 
			 *    unpausing after unparking the parked call.
			 *  - Unparking a call is done by dialing the parking_space, so there will be a delay 
			 *    (cca half a second) between dialing parking_space exten and showing connected to
			 *    the call which is  visible in UI as dialing into the parking_space exten. 
			 */
			await this.doAction({
				action: 'park'
			});
		}
		if (!call.parking_space) {
			console.warn("Could not unpark call, parking_space is not set");
			return;
		}
		await this.doAction({
			action: 'dial',
			number: call.parking_space
		});
	},

	/*
	 * Get current pre-calculated state of the call manager. This is updated
	 * automatically using updateState().
	 */
	getState() {
		return this.state || {
			real: false,
			devices: [],
			contacts: [],
			calls: [],
		};
	},

	/*
	 * Return the primary call that the user is interacting with.
	 */
	getActiveCall(maybe_unlinked = false) {
		let call = this.getState().active_call;

		return call && (maybe_unlinked || call.call_id) ? call : null;
	},

	/*
	 * Auto answer should be limited to once per a given call id.
	 */
	autoAnswer(call) {
		let session = call.webphone_session;
		if (session && session._status == session.C.STATUS_WAITING_FOR_ANSWER) {
			session.answer();
		}
	},

	shouldAutoAnswer(call) {
		/*
		 * We want to auto-answer first leg of calls that were originated via
		 * Customer API. To detect those calls we look for ringing peer
		 * with logged in user's exten and check originate_source
		 * property.
		 */
		let peers = (call.debug && call.debug.dbc) ? call.debug.dbc.peers : [];
		let me = peers.find(p => p.exten == this.exten && p.state == 'ringing' && p.originate_source == 'api')
		if (me) {
			return true;
		}

		return false;
	},

	muteBackgroundSessions() {
		/* only allow the active call to ring */
		for (let c of this.state.calls) {
			let is_active = c === this.state.active_call;
			let s = c.webphone_session;
			if (s) {
				s.__ringer.muted = s.__webphone.isRingingMuted();
			}
		}
	},

	/*
	 * Update call manager state from raw call data related to the user's
	 * currently selected extension returned by the server.
	 *
	 * The user doesn't have to have an extension seleted to have active
	 * calls.
	 */
	updateState() {
		if (!Application.session) {
			return;
		}
		let prev_state = this.state;

		this.state = this.calculateState(
			this.dialer_state || { 
				devices: [],
				contacts: [],
				calls: [],
			},
			Application.session.user,
			this.exten,
			this.state
		);

		this.muteBackgroundSessions();

		let active_call = this.state.active_call;
		if (active_call && this.shouldAutoAnswer(active_call)) {
			try {
				this.autoAnswer(active_call);
			} catch (ex) {
				/*
				 * Eat exception, because we don't want
				 * auto-answer failures to prevent state
				 * update message dispatch.
				 *
				 * Auto-answer is kind a nice to have,
				 * not a critical feature.
				 */
				console.log(ex);
			}
		}

		this.updateHeadsets(this.state);

		Application.dispatchMessage({
			type: 'CALL_MANAGER_CHANGE',
		});

		this.onStateChange(prev_state, this.state);
	},

	initHeadsets() {
		if (!Office.Headsets.isSupported()) {
			return;
		}

		this.mon(Office.Headsets, 'input', (h, usage, value) => {
			let state = this.getState();
			let call = state.active_call;
			if (!call) {
				Office.Headsets.syncStateAll();
				return;
			}

			let action;
			if (usage == 'phone-mute' && !call.mute_disabled) {
				action = call.muted ? 'unmute' : 'mute';
			}

			if (usage == 'hook-switch') {
				action = (call.status == 'incomming') ? 'answer' : 'hangup';
			}

			if (usage == 'flash' && !call.hold_disabled) {
				action = call.hold ? 'unhold' : 'hold';
			}

			if (action && this.canDo(action)) {
				this.doAction({action});
			} else {
				Office.Headsets.syncStateAll();
			}
		});
	},

	updateHeadsets(state) {
		if (!Office.Headsets.isSupported()) {
			return;
		}

		let call = state.active_call;

		Office.Headsets.setState({
			mute: call && !call.mute_disabled && call.muted,
			offhook: call && !['incomming', 'dialing'].includes(call.status),
			ring: call && ['incomming', 'dialing'].includes(call.status),
		});
	},

	onStateChange(prev, next) {
		if (prev && (!prev.active_call || !prev.active_call.call_id) &&
			next.active_call && next.active_call.status == 'incomming' && next.active_call.call_id) {
			let peer = next.active_call.peer;
			let contact = '???';
			if (peer) {
				contact = peer.number;

				if (peer.name) {
					contact += '\n' + peer.name;
				}

				if (peer.company) {
					contact += '\n' + peer.company;
				}
			}

			let queue = next.active_call.queue;
			let text = _('Příchozí hovor od {contact}').replace('{contact}', contact);
			if (queue) {
				text = _('Příchozí hovor ve frontě {queue} od {contact}')
					.replace('{queue}', queue)
					.replace('{contact}', contact);
			}

			Application.desktopNotification({
				title: document.title,
				text,
				backgroundOnly: true,
				role: 'incoming-call',
			});
		}

		if (!next.active_call || next.active_call.status != 'incomming') {
			Application.closeDesktopNotifications(c => c.role == 'incoming-call');
		}
	},

	/*
	 * Calculate call manager state based on server data and a few
	 * parameters.
	 *
	 * This function decides which devices are available and which one
	 * is selected, what call is "active" (meaning the primary call the
	 * user is interacting with) and status of an "extension".
	 *
	 * TODO: we need to project call state from the PoV of the relevenat
	 * exten/external_exten of a particular call. The call may be related
	 * to an exten before switch, and that needs to be hanled properly.
	 * It is not being handled correctly, atm.
	 */
	calculateState(data, user, exten, current_state) {
		let is_be_online = Application.socket.isReady();
		let webrtc_status = this.webphone ? this.webphone.getRegistrationStatus() : null;
		let sessions = this.getWebphoneSessions();
		let display_exten = exten;

		/*
		 * What server returns in devices array is the list of enabled
		 * devices.
		 *
		 * We generate devices status list for each enabled device based
		 * on that and based on status of the SIP registration in this
		 * window.
		 */
		let deviceNames = {
			htek: _('Htek'),
			sip: _('Telefon'),
			yealink: _('Yealink'),
			webrtc: _('WebPhone'),
		};

		let devices = data.devices.map(d => {
			let disabled = d.kind == 'webrtc' ? !webrtc_status || !webrtc_status.registered : !d.reachable;
			let error = webrtc_status ? webrtc_status.cause : null;
			let error_message = webrtc_status ? webrtc_status.cause_message : null;
			let reachable = !disabled;

			/*
			 * If backend is not online, all devices are unreachable
			 * by default.
			 */
			if (!is_be_online) {
				disabled = true;
				error = 'offline';
				error_message = _('Server je nedostupný. Momentálně nelze volat.');
			}

			return {
				id: d.kind,
				title: deviceNames[d.kind] || d.kind,
				disabled,
				reachable,
				error,
				error_message,
				device: d,
			};
		});

		/*
		 * Determine overall extension status. This is just a status of
		 * all devices combined. If one is reachable anywhere, extension
		 * is active.
		 */
		let some_device_reachable = devices.some(d => d.reachable) && Application.socket.isReady();
		let exten_status = exten && some_device_reachable ? 'ok' : 'fail';

		if (user.followme) {
			devices = [{
				id: 'followme',
				title: _('Externí linka'),
				disabled: false,
				error: null,
				error_message: null,
				device: {
					supported_actions: ['dial'],
				},
			}];

			display_exten = user.followme;
			exten_status = 'none';
		}

		/*
		 * Selected device is the one explicitly selected by the user if
		 * it can be found among enabled devices, or the first device in
		 * the list.
		 */
		let selected_device = devices.find(d => d.id == this.active_device) || devices[0];

		/*
		 * Determine if we have actionable non-webrtc device.
		 */
		let is_non_webrtc_device_actionable = devices.some(d => d.id != 'webrtc' && !d.disabled);

		/*
		 * Extension data returned by the server returns contact
		 * information separately. This is used to annotate number
		 * with a contact information.
		 */
		function deref_contact(number) {
			if ((typeof number == 'object' && number) || number == null) {
				return number;
			}

			let peer = data.contacts.find(c => c.number == number);
			if (!peer) {
				let queue = Office.QueueManager.getQueueContactInfo(number);
				if (queue) {
					return queue;
				}
			}

			return {
				number,
				name: peer ? peer.name : null,
				company: peer ? peer.company : null,
			};
		}

		function is_webrtc_endpoint(channel) {
			return (typeof channel == 'string') && !!channel.match(/^PJSIP\/99(\d{3,5})-/i);
		}

		/*
		 * Yes, we modify the original data, but deref_contact
		 * is idempotent so it shouldn't matter.
		 */
		let calls = data.calls.map(call => {
			/* We may have multiple linked_ids per call and websip
			 * phone may be associated with any one of them, and not
			 * necessarily the first one (call_id)
			 * 
			 * Collect all linked_ids so that we can pair the local
			 * webphone session to the call reliably.
			 *
			 * Determine what perspective to take in call data
			 * projection, based on the association of webphone the
			 * call's session is made from to a user extension.
			 */
			let linked_ids = new Set((call.peers || []).map(ch => ch.linked_id));
			let webphone_session_linked_id = Array.from(linked_ids).find(id => this.getWebphoneSession(id));
			let webphone_session = this.getWebphoneSession(webphone_session_linked_id);
			let my_exten = webphone_session ? webphone_session.__webphone.extension : exten;

			let c = project_call_to_exten(call, my_exten);
			if (!c) {
				return;
			}

			sessions = sessions.filter(s => !linked_ids.has(s.__linked_id));

			/*
			 * Fast track call status for incomming calls, so that
			 * the user gets connection status update before DBC
			 * processes the answer for incoming calls.
			 */
			if (webphone_session && webphone_session.isEstablished() && c.status == 'incomming') {
				c.status = 'connected';
			}

			/*
			 * Dereference contacts from number to {number, name, company}.
			 */
			c.peer = deref_contact(c.peer);
			c.chanspy_spied = deref_contact(c.chanspy_spied);

			if (c.transfer) {
				c.transfer.contact = deref_contact(c.transfer.contact);
			}

			c.webphone_session = webphone_session;
			c.my_exten = my_exten;
			c.has_non_webphone_channel = c.my_chans.some(ch => !is_webrtc_endpoint(ch.channel));
			c.actionable = !!((c.has_non_webphone_channel && is_non_webrtc_device_actionable) || c.webphone_session);

			let q = Office.QueueManager.getQueueContactInfo(c.queue);
			if (q) {
				c.queue_name = q.name;
			}

			return c;
		})

		/*
		 * Filter the list of all calls to calls we can do something about
		 * in this browser window.
		 */
		.filter(c => c && c.actionable);

		/*
		 * Append data about calls from local webphone sessions that don't have
		 * deep backend data available, yet.
		 */
		let early_calls = sessions.map(s => {
			let status;

			/*
			 *XXX: do we need to process holds correctly here, or do we not care?
			 */
			if (s.isInProgress()) {
				status = s.direction === 'incoming' ? 'incomming' : 'dialing';
			} else if (s.isEstablished()) {
				status = 'connected';
			}

			if (!status) {
				return;
			}

			return {
				status,

				peer: deref_contact(s.remote_identity ? s.remote_identity.uri.user : null),
				early_ts: s.__early_ts,
				start_ts: s.__start_ts,

				my_exten: s.__webphone.extension,
				webphone_session: s,
				actionable: true,
			};
		}).filter(v => v);

		calls.push(...early_calls);

		calls.forEach(c => {
			c.hold_disabled = (c.status != 'connected' && (c.status != 'hold' || c.transfer)) || !c.webphone_session;
			c.hold = c.webphone_session && c.webphone_session.isOnHold().local;
			
			c.park_disabled = c.status != 'connected';

			c.mute_disabled = c.status != 'connected' || !c.webphone_session;
			c.muted = c.webphone_session && c.webphone_session.isMuted().audio;
		});

		if (data.calls.length > 0) {
			console.log(data.calls, 'projected to', calls);
		}

		/*
		 * Calls that can be active.
		 */
		let foreground_calls = calls.filter(c => (c.status != 'hold' || c.transfer));

		/*
		 * Determine the active call for this dialer/browser window.
		 *
		 * Currently we allow to control only one call at a time. Any
		 * other calls are shown in the waiting calls list.
		 */
		let current_call = current_state && current_state.active_call;
		if (current_call) {
			current_call = foreground_calls.find(c => {
				/*
				 * Match by SIP session.
				 */
				if (c.webphone_session && current_call.webphone_session === c.webphone_session) {
					return true;
				}

				/*
				 * Match by call_id if available.
				 */
				if (c.call_id && current_call.call_id == c.call_id) {
					return true;
				}

				return false;
			});
		}

		/*
		 * Sticky behavior for the call that is currently shown
		 * as active in this browser window.
		 */
		let active_call = current_call || foreground_calls[0];

		/*
		 * If the active call is not controllable by the currently
		 * selected device, and the selection is disabled, change the
		 * selection temporarily.
		 */
		if (active_call && selected_device) {
			if (selected_device.id == 'webrtc' && !active_call.webphone_session) {
				selected_device = devices.find(d => d.id != 'webrtc') || selected_device;
			} else if (selected_device.id != 'webrtc' && !active_call.has_non_webphone_channel) {
				selected_device = devices.find(d => d.id == 'webrtc') || selected_device;
			}
		}

		return {
			...data,
			real: true,
			exten: display_exten,
			exten_status,
			devices,
			selected_device,
			calls,
			active_call,
		};
	},

	/*
	 * Call manager lifecycle:
	 *
	 * - start() starts the call manager when Agent/Office panel is open
	 * - end() ends the call manager when Agent/Office panel is closed
	 *
	 * Everything else happens within these bounds.
	 *
	 * External events:
	 * - DIALER_CHANGED: anything related to ongoing calls or device
	 *                   state has changed
	 * - ACTIVE_EXTENSION_SWITCH: selected extension changed
	 * - CLIENT_WEBSOCKET_STATUS_CHANGE: websocket got disconnected or
	 *                                   re-connected
	 *
	 * List of ongoing calls and device state is retrieved via
	 *   
	 *   GET /api/dialer/state
	 *
	 * Calls can be controlled via
	 *
	 *   POST /api/dialer/action
	 *
	 * Normally, /api/dialer/state will contain only calls relevant to the
	 * currently selected extension. Extension may change during the call,
	 * so we need to handle that case. In particular:
	 *
	 * - we record linked_id of the currently active call in this browser
	 *   window (from SIP session)
	 * - we send that linked_id to GET /api/dialer/state via linked_ids=...
	 *   query parameter
	 * - we register end of session callback on the active SIP session
	 *   that will:
	 *   - terminate the SIP registration once the call ends
	 *   - remove the calls linked_id from additionally monitored linked_ids
	 *     list
	 * - we create a new webphone registration for handling calls to the new
	 *   extension (we need to re-register to a new extension at the very
	 *   least)
	 *
	 * Other special case is when the current user has followme enabled
	 * on his currently active extension. When that happens, we need to
	 * unregister the webphone, and limit the available actions to those
	 * possible on mobile calls.
	 */

	async loadDialerState() {
		let signal = Utils.abortAndRetry(this, 'load-state');
		let extra_linked_ids = [...this.extra_linked_ids.keys()];

		try {
			this.dialer_state = await REST.GET('dialer/state', {
				extra_linked_ids: extra_linked_ids.join(','),
			}, {signal});
		} catch (ex) {
			this.dialer_state = null;
		}

		this.updateState();
	},

	getWebphoneSession(linked_id) {
		for (let webphone of [this.webphone, ...this.extra_webphones.values()]) {
			let s = webphone.getSession(linked_id);
			if (s) {
				return s;
			}
		}
	},

	getWebphoneSessions() {
		let sessions = [];

		for (let webphone of [this.webphone, ...this.extra_webphones.values()]) {
			 sessions.push(...webphone.sessions);
		}

		return sessions;
	},

	async associateMetadata(s) {
		/*
		 * When we receive linked_id from the server, we want to
		 * associate client side metadata with the call by sending
		 * them to the backend to store in database.
		 */

		let data = s.__send_metadata;
		if (data) {
			delete s.__send_metadata;

			await REST.POST('calls/' + s.__linked_id + '/associate-metadata', data);
		}
	},

	initWebphone() {
		/*
		 * Initialize SIP phone UA.
		 */
		this.webphone = new Office.WebPhone();
		this.webphone.muteRinging(this.ringingMuted);

		this.webphone.allowSession = (session, initial_request) => {
			if (session.direction != 'incoming' || !env.REJECT_WEBPHONE_CALLS_WHEN_BUSY) {
				return true;
			}

			if (this.getWebphoneSessions().some(s => !s.isOnHold().local)) {
				session.terminate({
					status_code: 503,
					reason_phrase: 'Service Unavailvable',
				});

				return false;
			}

			return true;
		},

		/*
		 * Translate webphone status changes to call manager
		 * status changes, because rest of the UI accesses the
		 * webphone status combined with call data from backend
		 * via CallManager.
		 */
		this.webphoneRegchangeListener = this.webphone.on('registrationchange', o => {
			this.updateState();
		});

		this.webphone.on('sessionchange', o => {
			this.updateState();
		});

		this.webphone.on('gotlinkedid', (o, session) => {
			this.associateMetadata(session);
		});

		this.webphone.on('log', (webphone, args) => {
			let el = document.querySelector('[data-gw="Office.Debugger"]');
			if (el) {
				el.gwComponent.renderItem({
					type: 'webphone',
					ts: new Date,
					ev: args,
				});
			}
		});
	},

	onSelectedExtensionSwitch() {
		let new_exten = Application.session ? Application.session.user.extension : null;
		if (new_exten == this.exten) {
			/*
			 * If the user doesn't have extension registered, do
			 * nothing.
			 */
			this.updateState();
			return;
		}

		this.exten = new_exten;

		let prev_webphone = this.webphone;
		if (prev_webphone && prev_webphone.sessions.size > 0) {
			/*
			 * If there are active sessions, monitor this webphone
			 * until all sessions end.
			 */
			this.extra_webphones.add(prev_webphone);

			for (let s of prev_webphone.sessions) {
				if (s.__linked_id) {
					this.extra_linked_ids.set(s.__linked_id, s);
				}

				let on_session_close = () => {
					if (s.__linked_id) {
						this.extra_linked_ids.delete(s.__linked_id);
					}

					if (prev_webphone.sessions.size == 0) {
						this.extra_webphones.delete(prev_webphone);
						this.updateExtensionSubscriptions();
						prev_webphone.destroy();
					}
				};

				s.on('failed', on_session_close);
				s.on('ended', on_session_close);
			}

			/*
			 * Un-register the contact, so that we no longer receive
			 * new calls on the old webphone.
			 */
			this.webphoneRegchangeListener.remove();
			prev_webphone.ua.unregister();

			/*
			 * Start new webphone for the new extension.
			 */
			this.initWebphone();
			this.webphone.connect();
		} else {
			if (this.webphone) {
				this.webphone.disconnect();
			} else {
				this.initWebphone();
			}

			this.webphone.connect();
		}

		this.updateExtensionSubscriptions();

		/*
		 * Load new dialer status data from the server.
		 */
		Utils.dedupeCalls(this, 'load-state', 100, () => this.loadDialerState());
	},

	updateExtensionSubscriptions() {
		Application.socketSend({
			type: 'SUBSCRIBE_EXTENSIONS',
			extensions: this.getMonitoredExtensions(),
		});
	},

	getMonitoredExtensions() {
		let extens = new Set;

		for (let p of this.extra_webphones) {
			if (p.extension) {
				extens.add(p.extension);
			}
		}

		if (this.webphone && this.webphone.extension) {
			extens.add(this.webphone.extension);
		}

		return [...extens];
	},

	isRingingMuted() {
		return this.ringingMuted;
	},

	muteRinging(muted) {
		this.ringingMuted = !!muted;

		if (this.webphone) {
			this.webphone.muteRinging(this.ringingMuted);
		}

		this.muteBackgroundSessions();

		Application.dispatchMessage({
			type: 'CALL_MANAGER_CHANGE',
		});
	},

	/*
	 * Start CallManager by connecting and registering a webphone.
	 */
	async start() {
		console.log('office started');

		this.active = true;
		this.onSelectedExtensionSwitch();
	},

	/*
	 * Stop CallManager by immediatelly disconnecting a webphone and
	 * terminating all calls (including all those extra ones).
	 */
	end() {
		console.log('office ended');

		this.active = false;

		Utils.abortDedupeCalls(this, 'load-state');
		Utils.abort(this, 'load-state');
		if (this.webphone) {
			this.webphone.disconnect();
		}
		for (let webphone of this.extra_webphones) {
			webphone.destroy();
		}
		this.extra_webphones = new Set;
		this.dialer_state = null;
		this.state = null;
		this.exten = null;

		this.updateExtensionSubscriptions();
	},

	onBusMessage(msg) {
		if (!this.active) {
			return;
		}

		if (msg.type == 'ACTIVE_EXTENSION_SWITCH') {
			this.onSelectedExtensionSwitch();
		}

		if (msg.type == 'CLIENT_WEBSOCKET_STATUS_CHANGE' && msg.ready) {
			this.updateExtensionSubscriptions();
		}

		if (msg.type == 'DIALER_CHANGED' || msg.type == 'CLIENT_WEBSOCKET_STATUS_CHANGE') {
			Utils.dedupeCalls(this, 'load-state', 100, () => this.loadDialerState());
		}
	},
});

Application.watchMessages(Office.CallManager.onBusMessage.bind(Office.CallManager));

// }}}
// {{{ Office.QueueManager

GW.define('Office.QueueManager', 'GW.Object', {
	singleton: true,

	initObject() {
		this.queues = [];
		this.queued_calls = {
			calls: [],
			details: {
				contacts: [],
				queues: [],
			},
		};
		this.queues_info = new Map();

		this.pause_types = [];
	},

	/*
	 * Queues list for when the user doesn't have an extension assigned.
	 */
	async loadQueues(loadQueueInfo) {
		let signal = Utils.abortAndRetry(this, 'load-queues');

		let new_exten = Application.session ? Application.session.user.extension : null;
		if (new_exten) {
			//let queues = await REST.GET('queues', null, {signal});
			let dialer_queues = await REST.GET('dialer/state/queues', null, {signal});

			this.queues = dialer_queues.queues;
			this.active_pause = dialer_queues.queue_pause;

			//this.queues = queues.filter(q => q.kind === 'agent' || q.kind === 'personal');

			if (loadQueueInfo) {
				Application.socketSend({
					type: 'LOAD_QUEUES_INFO',
				});
			}
		} else {
			this.queues = [];
			this.queues_info = new Map();
			this.active_pause = null;
		}

		this.mergeQueueInfo();

		Application.dispatchMessage({
			type: 'QUEUE_MANAGER_QUEUES_LIST_CHANGE',
		});
	},

	mergeQueueInfo() {
		this.queues.forEach(q => q.info = this.queues_info.get(q.queue));
	},

	getQueueContactInfo(number) {
		let queue = number != null ? this.queues.find(q => q.queue == number) : null;
		if (queue) {
			return {
				number,
				name: queue.name,
				company: null,
			};
		}

		return null;
	},

	getSubscribedQueues() {
		return this.queues.filter(q => q.state == 'subscribed' || q.state == 'paused');
	},

	async getCategoryHierarchy(queue, {signal} = {}) {
		return await REST.GET('queues/' + encodeURIComponent(queue) + '/call-category-hierarchy', null, {signal});
	},

	/*
	 * Modify a given phone number so that backend assigns a correct
	 * queue to an outgoing call.
	 */
	async makeQueueAssignmentNumber(queue, number, forcePrefix) {
		if (!queue) {
			return number;
		}

		if (env.IS_BCCP23) {
			// bccp23 supports prefixing on all queues
			return '*9*' + queue + '*' + number;
		} else {
			let q = this.queues.find(q => q.queue == queue);
			if ((q && q.supports_out_prefix) || forcePrefix) {
				// prefix the number with the queue - The queue
				// will be stripped and deep BE will assign
				// queue from the route name
				return queue + number;
			}
		}

		return number;
	},

	async joinQueue(queue, exten) {
		await REST.POST('queues/' + encodeURIComponent(queue) + '/exten/' + encodeURIComponent(exten) + '/join');
	},

	async leaveQueue(queue, exten) {
		await REST.POST('queues/' + encodeURIComponent(queue) + '/exten/' + encodeURIComponent(exten) + '/leave');
	},

	async joinAllQueues(exten) {
		await this.joinQueue('all', exten);
	},

	async leaveAllQueues(exten) {
		await this.leaveQueue('all', exten);
	},

        // {{{ Pauses

	async loadPauseTypes() {
		let signal = Utils.abortAndRetry(this, 'pause-types-reload');

		this.pause_types = await REST.GET('pause-types', null, {signal});

		Application.dispatchMessage({
			type: 'QUEUE_MANAGER_PAUSE_TYPES_CHANGE',
		});
	},

	async pauseExtension(exten, pause_type_id) {
		await REST.POST('queues/all/exten/' + encodeURIComponent(exten) + '/pause', pause_type_id ? {
			pause_type: pause_type_id,
		} : undefined);
	},

	async extendExtensionPause(exten) {
		await REST.POST('queues/all/exten/' + encodeURIComponent(exten) + '/extend-pause');
	},

	async unpauseExtension(exten) {
		await REST.POST('queues/all/exten/' + encodeURIComponent(exten) + '/unpause');
	},

	getPauseTypes() {
		return this.pause_types;
	},

	start() {
		this.active = true;

		this.loadQueues(true);
		this.loadPauseTypes();
	},

	end() {
		this.active = false;

		Utils.abortAll(this);
	},

	// }}}

	onBusMessage(msg) {
		if (!this.active) {
			return;
		}

		/*
		 * Queue info update stream was disconnected, we need to
		 * re-sync.
		 */
		if (msg.type === 'CLIENT_SESSION_CHANGE' ||
			msg.type === 'ACTIVE_EXTENSION_SWITCH' ||
			msg.type === 'CLIENT_WEBSOCKET_STATUS_CHANGE' && msg.ready ||
			msg.type === 'QUEUES_CHANGED') {

			this.loadQueues(msg.type !== 'QUEUES_CHANGED');
		}

		if (msg.type === 'QUEUES_INFO_CHANGED') {
			this.queues_info = new Map(msg.data.map(i => [i.queue, i]));

			this.mergeQueueInfo();

			Application.dispatchMessage({
				type: 'QUEUE_MANAGER_QUEUES_LIST_CHANGE',
			});
		}

		if (msg.type === 'WAITING_CALLS_CHANGED') {
			this.queued_calls = msg.data;

			Application.dispatchMessage({
				type: 'QUEUE_MANAGER_QUEUED_CALLS_CHANGE',
			});
		}

		if (msg.type === 'CLIENT_SESSION_CHANGE') {
			this.loadPauseTypes();
		}
	},

});

Application.watchMessages(Office.QueueManager.onBusMessage.bind(Office.QueueManager));

// }}}

// Dialer UI:

// {{{ Office.DeviceSwitcher

GW.define('Office.DeviceSwitcher', 'Widgets.ItemSwitcher', {
	cls: 'button-switcher small',
	itemCls: 'button',
});

// }}}
// {{{ Office.DialerInput

/*
 * Dialer input states:
 * - idle: input + green call initiaition button, autocomplete on entry
 * - disabled: input and button disabled
 */
GW.define('Office.DialerInput', 'GW.Component', {
	placeholder: _('Vytočit číslo'),

	initComponent() {
		this.el = document.gwCreateElement({
			className: 'js-msgbus',
			children: [{
				ref: 'input',
				nodeName: 'input',
				className: 'large',
				placeholder: this.placeholder,
				'on:input': this.onInput.bind(this),
				'on:focus': this.onInputFocus.bind(this),
				'on:blur': this.onInputBlur.bind(this),
				'on:keydown': this.onInputKeyDown.bind(this),
				'on:click': this.onInputClick.bind(this),
			}, {
				ref: 'statusIcon',
				className: 'hidden status-icon',
			}, {
				ref: 'controls',
				className: 'controls',
				'on*:click': ev => {
					let el = ev.target.closest('button');
					if (el && !el.disabled) {
						this.fire('buttonclick', el.$data);
					}
				},
			}],
		}, this);

		this.setButtons([{
			id: 'dial',
			icon: 'phone-filled',
			cls: 'success icon-only',
			title: _('Vytočit'),
		}]);
	},

	emulateKeyPress(key) {
		if (this.input.disabled) {
			return;
		}

		let v = this.input.value;

		if (key == 'del') {
			if (v.length > 0) {
				v = v.substr(0, v.length - 1);
			}
		} else if (key == 'clr') {
			v = '';
		} else {
			v += key;
		}

		this.input.value = v;
		if (!Application.isMobileUI()) {
			this.input.focus();
			this.input.setSelectionRange(v.length, v.length);
		}
	},

	onInputFocus(ev) {
		ev.target.select();
	},

	onInputClick(ev) {
		let query = this.input.value.trim();
		if (!query) {
			this.loadSuggestions(query);
		}
	},

	onInputBlur(ev) {
		if (this.input.readOnly) {
			return;
		}

		//this.input.value = '';
		//this.input.blur();
		this.hideSuggestions();
	},

	onInput(ev) {
		let query = this.input.value.trim();
		if (query) {
			this.loadSuggestions(query);
		} else {
			this.hideSuggestions();
		}
	},

	onInputKeyDown(ev) {
		let el;

		if (this.input.readOnly) {
			return;
		}

		if (!this.suggestionsVisible()) {
			switch (ev.key) {
			case 'ArrowDown':
				ev.preventDefault();
				this.loadSuggestions();
				break;
			case 'Enter':
				ev.preventDefault();
				this.fire('submit', this.getNumber());
				break;
			}

			return;
		}

		switch (ev.key) {
		case 'ArrowDown':
			ev.preventDefault();

			el = this.suggestions.querySelector('.selected');
			el = el && el.nextElementSibling;
			el = el || this.suggestions.querySelector('.suggestion');

			if (el && el.matches('.suggestion')) {
				Utils.selectListItem(this.suggestions, '.suggestion', 'selected', i => i === el);
			}
			break;

		case 'ArrowUp':
			ev.preventDefault();

			el = this.suggestions.querySelector('.selected');
			el = el && el.previousElementSibling;

			if (el && el.matches('.suggestion')) {
				Utils.selectListItem(this.suggestions, '.suggestion', 'selected', i => i === el);
			}
			break;

		case 'Enter':
			el = this.suggestions.querySelector('.selected');
			if (el) {
				ev.preventDefault();

				this.onSuggestionSelect(el.$data);
			} else {
				ev.preventDefault();
				this.hideSuggestions();
				this.fire('submit', this.getNumber());
			}
			break;

		case 'Escape':
			ev.preventDefault();
			this.input.value = '';
			this.hideSuggestions();
			break;
		}
	},

	'before:releaseComponent'() {
		this.suggestions && this.suggestions.remove();
	},

	onSuggestionSelect(contact) {
		this.input.value = '';
		this.input.focus();
		this.hideSuggestions();

		this.fire('select', contact);
	},

	suggestionsVisible() {
		return this.suggestions && !this.suggestions.classList.contains('hidden');
	},

	async loadSuggestions(query) {
		let signal = Utils.abortAndRetry(this, 'suggest');
		let match = TextUtils.createMatchAll(query || '');
		let contacts = [];

		if (query) {
			contacts = await REST.GET('contacts', {limit: 30, q: query}, {signal});

			contacts = contacts.flatMap(c => (c.phones || []).map(phone => ({
				number: phone,
				name: c.name,
				company: c.company,
			})));
		} else {
			[contacts] = await Utils.cache(this, 'last-called', 60000, REST.GET, 'contacts/last-called', null, {signal});
		}

		contacts = contacts.filter(v => match(v.number + ' ' + (v.company || '') + ' ' + (v.name || '')));

		if (contacts.length > 0) {
			this.showSuggestions(contacts);
		} else {
			this.hideSuggestions();
		}
	},

	showSuggestions(list) {
		if (!this.suggestions) {
			this.suggestions = document.body.gwCreateChild({
				className: 'dialer-input-suggestions',
				children: [{
					className: 'title',
					textContent: _('Poslední hovory'),
				}, {
					ref: 'suggestionsContent',
					'on*:mousedown': ev => {
						let el = ev.target.closest('.suggestion');
						if (el) {
							this.onSuggestionSelect(el.$data);
						}
					},
				}],
				hidePopup() {
					this.classList.add('hidden');
				},
			});
		}

		this.suggestions.classList.remove('hidden');

		this.suggestions.suggestionsContent.gwReplaceChildren(list.map(c => {
			return {
				$data: c,
				nodeName: 'a',
				href: '#',
				className: 'suggestion',
				children: [{
					className: 'number',
					textContent: c.number,
				}, {
					className: 'info',
					textContent: [c.name, c.company].filter(v => v).join(', '),
				}],
			};
		}));

		this.suggestions.style.width = this.el.offsetWidth + 'px';

		PopupManager.show(this.suggestions, {
			anchorEl: this.el,
			forceAxis: 'vertical',
			preferReadingOrder: true,
			resizeNotFitting: true,
			disableMobileUIPopover: true,
			focus: false,
		});
	},

	hideSuggestions() {
		if (!this.suggestions) {
			return;
		}

		PopupManager.hide(this.suggestions);
	},

	setDisabled(disabled) {
		this.disabled = !!disabled;
		this.el.classList.toggle('disabled', this.disabled);

		this.input.disabled = !!disabled;
		for (let el of this.controls.children) {
			el.disabled = !!disabled;
		}

		if (disabled) {
			this.hideSuggestions();
		}
	},

	setButtons(btns) {
		btns = btns.map(b => {
			return {
				...b,
				disabled: b.disabled || this.disabled || false,
			};
		});

		Utils.syncRenderList({
			ct: this.controls,
			list: btns,
			keepSelection: false,
			onAdd(b) {
				return document.gwCreateElement({
					$data: b,
					nodeName: 'button',
					'attr:data-action': b.id,
					disabled: !!b.disabled,
					className: b.cls,
					children: [Utils.useIcon(b.icon)],
					title: b.title,
				});
			},
		});
	},

	setStatusIcon(icon, status) {
		if (icon) {
			this.statusIcon.gwReplaceChildren([icon !== "dialing-animation" ? Utils.useIcon(icon) : {
				nodeName: 'img',
				src: APP_BASE + 'img/dialing-animation.gif',
				className: 'icon',
			}]);

			this.statusIcon.classList.toggle('hidden', false);
			this.statusIcon.dataset = status;
		} else {
			this.statusIcon.classList.toggle('hidden', true);
			this.statusIcon.textContent = '';
			this.statusIcon.dataset = '';
		}
	},

	sanitizeNumber(s) {
		return String(s).replace(/[^0-9+*#]/g, '');
	},

	getNumber() {
		return this.sanitizeNumber(this.input.value);
	},

	setNumber(val) {
		this.input.value = this.sanitizeNumber(val || '');
	},

	onBusMessage(msg) {
		if (msg.type == 'CALL_ENDED') {
			Utils.clearCache(this);
		}
	},
});

// }}}
// {{{ Office.CallControl

/*
 * Call control box states:
 *
 * - dialing: callee info, dialing indication, red hangup button
 * -
 */
GW.define('Office.CallControl', 'GW.Component', {
	initComponent() {
		this.el = document.gwCreateElement({
			className: 'office-call-control',
			children: [{
				className: 'main-box',
				children: [{
					ref: 'statusEl',
					className: 'status',
				}, {
					ref: 'timerEl',
					className: 'timer',
				}, {
					ref: 'peerEl',
					className: 'peer',
				}, {
					className: 'controls',
					children: [{
						ref: 'statusIcon',
						className: 'status-icon',
					}, {
						ref: 'controlsEl',
						className: 'buttons',
					}],
					'on*:click': ev => {
						let el = ev.target.closest('button');
						if (el && !el.disabled) {
							this.fire('buttonclick', el.$data);
						}
					},
				}, {
					ref: 'forwardingInput',
					xtype: 'Office.DialerInput',
					placeholder: _('Přepojit na číslo'),
					'on:buttonclick': (c, btn) => {
						c.setNumber(c.getNumber());
						this.fire('buttonclick', btn);
					},
					'on:select': (c, contact) => {
						c.setNumber(contact.number);
					},
				}],
			}, {
				ref: 'callActions',
				allowDTMF: true,
				xtype: 'Office.CallActions',
				'on:dialerkey': (c, key) => this.forwardingInput.emulateKeyPress(key),
				'on:dtmf': (c, key) => {
					if (Office.CallManager.canDo('dtmf')) {
						Office.CallManager.doAction({action: 'dtmf', number: key});
						this.callActions.addDTMFKey(key);
					}
				},
				'on:action': (c, action) => {
					if (Office.CallManager.canDo(action)) {
						Office.CallManager.doAction({action});
					}
				},
			}],
		}, this);

		Utils.interval(this, 'timer-update', 1000, () => this.renderCallTimer());
	},

	renderCallTimer() {
		let c = this.call;

		if (!c || (!c.start_ts && !c.early_ts)) {
			this.timerEl.textContent = '';
			return;
		}

		this.timerEl.textContent = c.start_ts ? Utils.getElapsedTime(c.start_ts) : Utils.getElapsedTime(c.early_ts);
	},

	setButtons(btns) {
		this.controlsEl.gwReplaceChildren(btns.map(b => {
			return {
				$data: b,
				nodeName: 'button',
				'attr:data-action': b.id,
				disabled: !!b.disabled,
				className: b.cls,
				children: [Utils.useIcon(b.icon)],
				title: b.title,
			};
		}));
	},

	setStatusIcon(icon, status) {
		if (icon) {
			this.statusIcon.gwReplaceChildren([Utils.useIcon(icon)]);
			this.statusIcon.classList.toggle('hidden', false);
			this.statusIcon.dataset = status;
		} else {
			this.statusIcon.classList.toggle('hidden', true);
			this.statusIcon.textContent = '';
			this.statusIcon.dataset = '';
		}
	},
	
	async renderButtonAddContact(contactTextEl, peerNumber, buildContactText) {
		const signal = Utils.abortAndRetry(this, 'contact-groups');
		const [groups] = await Utils.cache(this, 'contact-groups', 10000, REST.GET, 'contact-groups', null, {signal});
		
		const editableGroups = groups.filter(g => g.editable);
		if (!editableGroups.length) {
			return;
		}

		contactTextEl.gwReplaceChildren([{
			className: 'secondary xs button-add-contact icon-left',
			nodeName: 'button',
			children: [
				Utils.useIcon('plus'),
				_('Přidat do adresáře')
			],
			'on:click': e => {
				new Office.ContactEditDialog({
					contactGroups: editableGroups,
					title: _('Nový kontakt'),
					contact: { // prefill known data (no id, contact not saved)
						phones: [peerNumber]
					},
					'on:aftersave': (c, data) => {
						contactTextEl.textContent = buildContactText(data);
					}
				});
			}
		}]);
	},

	/*
	 * Configure call control according to call status. Expects:
	 *
	 * - call:
	 *   - status: connected / dialing / hold / disconnected / incomming
	 *   - peer: {number, name, company}
	 *   - queue
	 *   - start_ts
	 *   - transfer:
	 *     - status: dialing / connected
	 *     - contact: {number}
	 */
	setData(call) {
		let statusTexts = {
			dialing: _('Vytáčím'),
			connected: _('Probíhající hovor'),
			incomming: _('Příchozí hovor'),
			hold: _('Odložený'),
			musichold: _('Odložený'),
		};

		let statusText = statusTexts[call.status] || call.status;
		if (call.transfer) {
			statusText = _('Přepojovaný');
		}

		if (call.chanspy_spied) {
			statusText = {
				connected: _('Probíhající odposlech'),
				incomming: _('Příchozí odposlech'),
			}[call.status] || statusText;
		}

		this.el.dataset.status = call.status;

		this.statusEl.textContent = statusText;
		if (call.status == 'dialing') {
			this.statusEl.gwCreateChild({
				nodeName: 'img',
				src: APP_BASE + 'img/dialing-animation.gif',
				className: 'icon',
			});
		}

		if (call.status == 'musichold' || call.transfer) {
			this.setStatusIcon('mute', 'muted');
		} else {
			this.setStatusIcon();
		}

		const peer = call.chanspy_spied || call.peer;
		if (peer) {
			this.peerEl.gwReplaceChildren([{
				className: 'number',
				textContent: peer.number,
			}]);
			
			const buildContactText = peer => [peer.name, peer.company].filter(v => v).join(', ').trim(); 
			
			const contactText = buildContactText(peer);
			const refs = {};
			this.peerEl.gwCreateChild({
				ref: 'contactTextEl',
				className: 'contact',
				textContent: contactText,
			}, refs);
			
			if (!contactText) {
				// no text, either the contact does not exist or has empty data
				this.renderButtonAddContact(refs.contactTextEl, peer.number, buildContactText);
			}
		} else {
			this.peerEl.textContent = '';
		}

		if (call.queue) {
			this.peerEl.gwCreateChild({
				className: 'queue',
				textContent: _('Fronta') + ' ' + call.queue + (call.queue_name ? ` - ${call.queue_name}` : ''),
			});
		}

		this.peerEl.classList.toggle('hidden', !call.peer);
		this.timerEl.classList.toggle('hidden', !call.start_ts && !call.early_ts);

		// call control buttons
		if (call.status == 'incomming') {
			// incomming unanswered call
			this.setButtons([{
				id: 'hangup',
				cls: 'error icon-only',
				icon: 'phone-end-filled',
				disabled: !Office.CallManager.canDo('hangup'),
				title: _('Odmítnout'),
			}, {
				id: 'answer',
				cls: 'success icon-only',
				icon: 'phone-filled',
				disabled: !Office.CallManager.canDo('answer'),
				title: _('Odpovědět'),
			}]);
		} else {
			// outgoing call, connected call
			this.setButtons([{
				id: 'hangup',
				icon: 'phone-end-filled',
				cls: 'error icon-only',
				// during transfer, hangup is disabled
				disabled: !!call.transfer,
				title: _('Zavěsit'),
			}]);
		}

		this.setTransferData(call);

		this.callActions.setState({
			hold_disabled: call.hold_disabled,
			hold: call.hold,

			mute_disabled: call.mute_disabled,
			muted: call.muted,

			dtmf_disabled: !Office.CallManager.canDo('dtmf'),
		});
		this.callActions.refreshDialer();

		if (call.status == 'disconnected') {
			this.callActions.clearDTMFFeedback();
		}

		this.call = call;
		this.renderCallTimer();
	},

	/*
	 * Configure transfer input control according to call status.
	 * Expects:
	 *
	 * - call:
	 *   - status: connected / [other]
	 *   - transfer:
	 *     - status: dialing / connected
	 *     - contact: {number}
	 */
	setTransferData(call) {
		let input = this.forwardingInput;
		let transfer = call.transfer;

		if (call.status == 'disconnected') {
			input.setNumber('');
		}

		// call transfer buttons and input
		if (transfer && transfer.status == 'dialing') {
			input.setButtons([{
				id: 'cancel-transfer',
				icon: 'phone-end-filled',
				cls: 'error icon-only',
				title: _('Zavěsit'),
			}]);

			input.input.readOnly = true;
		} else if (transfer && transfer.status == 'connected') {
			input.setButtons([{
				id: 'complete-transfer',
				icon: 'fi-corner-up-right',
				cls: 'success icon-only',
				title: _('Dokončit přepojení'),
			}, {
				id: 'cancel-transfer',
				icon: 'phone-end-filled',
				cls: 'error icon-only',
				title: _('Zrušit přepojení'),
			}]);

			input.input.readOnly = true;
		} else {
			// no transfer is ongoing
			input.setButtons([{
				id: 'blind-transfer',
				icon: 'arrow-right',
				cls: 'success-secondary icon-only',
				disabled: call.status != 'connected',
				title: _('Přepojit'),
			}, {
				id: 'attended-transfer',
				icon: 'fi-corner-up-right',
				cls: 'success-secondary icon-only',
				disabled: call.status != 'connected',
				title: _('Přepojit s odložením'),
			}]);

			input.input.readOnly = false;
		}

		if (transfer && transfer.status == 'dialing') {
			input.setStatusIcon('dialing-animation', 'dialing');
		} else {
			input.setStatusIcon();
		}

		if (transfer && transfer.contact) {
			input.setNumber(transfer.contact.number);
		}

		input.setDisabled(!transfer && !(call.status == 'connected' && call.call_id));
	},
});

// }}}
// {{{ Office.NumericKeyboard

GW.define('Office.NumericKeyboard', 'GW.Component', {
	initComponent() {
		this.el = document.gwCreateElement({
			className: 'office-numeric-keyboard',
			'on*:click': ev => {
				let btn = ev.target.closest('button');
				if (btn && !btn.disabled && btn.dataset.action) {
					this.fire('dialerkey', btn.dataset.action);
				}
			},
		}, this);

	},

	setType(type) {
                this.el.textContent = '';

		this.renderRow([{
			action: '1',
		}, {
			action: '2',
		}, {
			action: '3',
		}, {
			action: 'del',
			icon: 'backspace',
			disabled: type == 'dtmf',
		}]);

		this.renderRow([{
			action: '4',
		}, {
			action: '5',
		}, {
			action: '6',
		}, {
			action: '*',
			icon: 'asterisk',
		}]);

		this.renderRow([{
			action: '7',
		}, {
			action: '8',
		}, {
			action: '9',
		}, {
			action: '#',
		}]);

		this.renderRow([{
			invisible: true,
		}, {
			action: '0',
		}, {
			invisible: true,
		}, {
			action: '+',
			icon: 'plus-bold',
			disabled: type == 'dtmf',
		}, ]);
	},

	renderRow(btns) {
		this.el.gwCreateChild({
			className: 'dialer-row',
			children: btns.map(b => this.renderButton(b)),
		});
	},

	renderButton(b) {
		if (b.hidden) {
			return;
		}

		return {
			$def: b,
			nodeName: 'button',
			'attr:data-action': b.action || undefined,
			className: b.invisible ? 'invisible' : undefined,
			disabled: b.disabled,
			children: [b.icon ? Utils.useIcon(b.icon) : {
				textContent: b.text || b.action || '',
			}],
		};
	},

	setDisabled(disabled) {
		this.disabled = !!disabled;

		for (let el of this.el.querySelectorAll('button')) {
			el.disabled = disabled || el.$def.disabled;
		}
	},
});

// }}}
// {{{ Office.CallActions

GW.define('Office.CallActions', 'GW.Component', {
	allowDTMF: false,

	initComponent() {
		this.el = document.gwCreateElement({
			className: 'office-call-actions',
			children: [{
				className: 'header',
				children: [{
					':skip': true,
					ref: 'confAddBtn',
					nodeName: 'button',
					title: _('Přidat hovor'),
					className: 'secondary',
					children: [Utils.useIcon('phoneplus')],
					'attr:data-action': 'confadd',
				}, {
					ref: 'holdBtn',
					nodeName: 'button',
					className: 'secondary',
					children: [Utils.useIcon('phonepause')],
				}, {
					ref: 'parkBtn',
					title: _('Zaparkovat hovor'),
					nodeName: 'button',
					className: 'secondary',
					children: [Utils.useIcon('phonepark')],
				}, {
					ref: 'micBtn',
					nodeName: 'button',
					children: [Utils.useIcon('microphone')],
				}, {
					':skip': !this.allowDTMF,
					ref: 'numDialerBtnType',
					nodeName: 'button',
					children: [Utils.useIcon('apps')],
					'on:click': ev => this.toggleDialer('type'),
				}, {
					':skip': !this.allowDTMF,
					ref: 'numDialerBtnDTMF',
					nodeName: 'button',
					children: [Utils.useIcon('dialerdtmf')],
					'on:click': ev => this.toggleDialer('dtmf'),
				}, {
					':skip': !!this.allowDTMF,
					ref: 'numDialerBtnType',
					nodeName: 'button',
					children: [Utils.useIcon('apps')],
					'on:click': ev => this.toggleDialer('type'),
				}],
				'on*:click': ev => {
					let btn = ev.target.closest('button');
					if (btn && !btn.disabled && btn.dataset.action) {
						this.fire('action', btn.dataset.action);
					}
				},
			}, {
				ref: 'keypressFeedbackCt',
				className: 'keypress-feedback',
				'attr:text-no-entry': _('Tónová volba (DTMF)'),
			}, {
				ref: 'keyboard',
				xtype: 'Office.NumericKeyboard',
				'on:dialerkey': (c, key) => {
					let dialer = this.getDialerState();
					if (dialer == 'type') {
						this.fire('dialerkey', key);
					} else {
						this.fire('dtmf', key);
					}
				},
			}],
		}, this);

		if (this.state) {
			this.setState(this.state);
		}

		this.refreshDialer();
	},

	addDTMFKey(key) {
		this.keypressFeedbackCt.textContent += key;
		if (this.keypressFeedbackCt.scrollWidth > this.keypressFeedbackCt.clientWidth) {
			this.keypressFeedbackCt.scrollTo(this.keypressFeedbackCt.scrollWidth, 0);
		}
	},

	clearDTMFFeedback() {
		this.keypressFeedbackCt.textContent = '';
	},

	setDTMFEnabled(en) {
		this.dtmfKeysEnabled = !!en;
	},

	toggleDialer(target) {
		let dialer = this.getDialerState();

		Utils.setLocalState({key: 'dialer-expanded'}, dialer != target ? target : null);
		this.refreshDialer();
	},

	getDialerState() {
		let dialer = Utils.getLocalState({key: 'dialer-expanded'}, Application.isMobileUI() ? 'type' : null);
		if (dialer != null && !['dtmf', 'type'].includes(dialer)) {
			dialer = 'type';
		}

		if (!this.allowDTMF && dialer) {
			dialer = 'type';
		}

		return dialer;
	},

	refreshDialer() {
		let dialer = this.getDialerState();

		this.numDialerBtnType.title = dialer == 'type' ? _('Skrýt dialer') : _('Zobrazit dialer');
		this.numDialerBtnType.className = dialer == 'type' ? 'primary hover' : 'secondary';

		if (this.allowDTMF) {
			this.numDialerBtnDTMF.title = dialer == 'dtmf' ? _('Skrýt dialer (tónová volba)') : _('Zobrazit dialer (tónová volba)');
			this.numDialerBtnDTMF.className = dialer == 'dtmf' ? 'primary hover' : 'secondary';
		}

		this.keyboard.el.gwHide(!dialer);
		if (dialer) {
			this.keyboard.setType(dialer);
		}

		if (dialer == 'dtmf') {
			this.keyboard.setDisabled(!this.dtmfKeysEnabled);
		}

		this.keypressFeedbackCt.classList.toggle('hidden', !dialer || dialer == 'type');
		this.keypressFeedbackCt.classList.toggle('active', dialer == 'dtmf' && this.dtmfKeysEnabled);
	},

	/*
	 * - hold = true/false
	 * - muted = true/false
	 * - disabled = true/false (disables all buttons except for keyboard
	 *   switch)
	 * - dtmf_disabled - true/false (disables DTMF input keyboard keys)
	 */
	setState(data) {
		this.holdBtn.title = data.hold ? _('Zrušit odložení') : _('Odložit hovor');
		this.holdBtn.dataset.action = data.hold ? 'unhold' : 'hold';
		this.holdBtn.className = data.hold ? 'error' : 'secondary';
		this.holdBtn.disabled = !!data.disabled || !!data.hold_disabled;
		
		this.parkBtn.dataset.action = 'park';
		this.parkBtn.disabled = !!data.disabled || !!data.park_disabled;

		this.micBtn.title = data.muted ? _('Zapnout mikrofon') : _('Vypnout mikrofon');
		this.micBtn.gwReplaceChildren([Utils.useIcon(data.muted ? 'microphoneoff' : 'microphone')]);
		this.micBtn.className = data.muted ? 'error' : 'secondary';
		this.micBtn.dataset.action = data.muted ? 'unmute' : 'mute';
		this.micBtn.disabled = !!data.disabled || !!data.mute_disabled;

		this.setDTMFEnabled(!data.dtmf_disabled);
	},
});

// }}}
// {{{ Office.Dialer

GW.define('Office.Dialer', 'GW.Component', {
	
	held_calls: [],
	parked_calls: {
		calls: [],
		details: {},
	},

	initComponent() {
		this.el = document.gwCreateElement({
			className: 'office-dialer panel-container js-msgbus',
			socketSubscriptions: ['parked_calls'],
			children: [{
				className: 'header',
				children: [{
					ref: 'extensionStatus',
					className: 'extension-status',
					children: [Utils.useIcon('dot')],
				}, {
					ref: 'extensionEl',
					className: 'extension',
				}, {
					ref: 'deviceSwitcher',
					xtype: 'Office.DeviceSwitcher',
					'on:beforeselect': (c, btn) => this.onDeviceSelect(btn),
				}, {
					ref: 'ringingMuteEl',
					nodeName: 'button',
					className: 'hidden',
					'on:click': ev => {
						Office.CallManager.muteRinging(!Office.CallManager.isRingingMuted());
					},
				}]
			}, {
				ref: 'deviceStatus',
				className: 'device-status hidden',
				'attr:data-status': 'error',
			}, {
				ref: 'inputOptions',
				className: 'input-options',
			}, {
				ref: 'dialerInput',
				xtype: 'Office.DialerInput',
				'on:select': (c, contact) => {
					this.callManagerAction({action: 'dial', number: c.sanitizeNumber(contact.number)});
				},
				'on:submit': (c, number) => {
					// clear the input
					c.setNumber('');

					this.callManagerAction({action: 'dial', number});
				},
				'on:buttonclick': (c, btn) => {
					this.callManagerAction({action: btn.id, number: c.getNumber()});
				},
			}, {
				ref: 'callControl',
				xtype: 'Office.CallControl',
				'on:buttonclick': (c, btn) => {
					this.callManagerAction({action: btn.id, number: c.forwardingInput.getNumber()});
				},
			}, {
				ref: 'callActionsGlobal',
				xtype: 'Office.CallActions',
				'on:dialerkey': (c, key) => this.dialerInput.emulateKeyPress(key),
				state: {
					disabled: true,
				},
			}, {
				ref: 'heldAndParkedCallsCt',
				className: 'office-held-and-parked-calls hidden',
				children: [{
					className: 'title',
					textContent: _('Odložené a zaparkované hovory'),
				}, {
					ref: 'heldAndParkedCallsList',
					className: 'office-dialer-calls-list',
				}],
			}, {
				ref: 'waitingCallsCt',
				className: 'office-waiting-calls hidden',
				children: [{
					className: 'title',
					textContent: _('Čekající hovory'),
				}, {
					ref: 'waitingCallsList',
					className: 'office-dialer-calls-list',
				}],
			}],
		}, this);

		Utils.interval(this, 'timer-update', 1000, () => this.updateWaitingTimers());

		Application.dispatchMessage({
			type: 'CALL_MANAGER_CHANGE',
		});
		Application.socketSend({
			type: 'LOAD_PARKED_CALLS',
		});

		this.monDom(document, 'keydown', this.onDocKeyDown.bind(this), true);
	},
	
	callManagerAction(args) {
		// allow to modify CallManager actions in inherited components
		Office.CallManager.doAction(args);
	},

	releaseComponent() {
		if (this.debugger) {
			this.debugger.destroy();
		}
	},

	onDocKeyDown(ev) {
		if (Utils.describeKeyEvent(ev) == 'Ctrl_b') {
			ev.preventDefault();

			if (this.debugger) {
				this.debugger.destroy();
				this.debugger = null;
			} else {
				this.debugger = new Office.Debugger({});
				document.body.append(this.debugger.el);
			}
		}
	},

	renderDisconnectedState() {
		this.deviceSwitcher.setDisabled(false);

		this.dialerInput.setDisabled(!Office.CallManager.canDo('dial'));
		this.dialerInput.el.gwHide(false);

		this.callControl.el.classList.toggle('hidden', true);
		this.callControl.setData({
			status: 'disconnected',
		});
	},

	renderCall(call) {
		this.deviceSwitcher.setDisabled(!(!call.transfer && call.status == 'incomming'));

		this.dialerInput.setDisabled(true);
		this.dialerInput.el.gwHide(true);

		this.callControl.el.classList.toggle('hidden', false);
		this.callControl.setData(call);
	},

	updateWaitingTimers() {
		for (let el of this.waitingCallsList.querySelectorAll('.timer')) {
			el.textContent = el.$start_ts ? Utils.getElapsedTime(el.$start_ts) : '';
		}
	},

	renderWaitingCalls(calls) {
		this.waitingCallsList.gwReplaceChildren(calls.map(c => {
			let peer = c.peer || {
				number: '???',
			};

			return {
				className: 'call',
				children: [{
					className: 'header',
					children: [{
						className: 'number',
						textContent: peer.number,
					}, {
						$start_ts: c.early_ts || c.start_ts,
						className: 'timer',
					}]
				}, {
					className: 'info',
					textContent: [peer.name, peer.company].filter(v => v).join(', '),
				}, {
					':skip': !c.queue,
					className: 'queue',
					textContent: _('Fronta') + ' ' + c.queue,
				}],
			};
		}));

		this.updateWaitingTimers();
		this.waitingCallsCt.classList.toggle('hidden', calls.length == 0);
	},

	renderHeldAndParkedCalls() {
		
		const held_calls = this.held_calls;
		const parked_calls = this.parked_calls;
		const active_call = Office.CallManager.getState().active_call;
		
		this.heldAndParkedCallsList.gwReplaceChildren([
			...parked_calls.calls.map(call => {
				const contact = parked_calls.details && parked_calls.details.contacts.find(c => c.phone == call.caller);
				return {
					className: 'call',
					children: [{
						className: 'controls',
						children: [{
							className: 'parking-space',
							textContent: call.parking_space,
						}, {
							nodeName: 'button',
							className: 'secondary small icon-only',
							children: [active_call ? Utils.useIcon('swap') : Utils.useIcon('fi-corner-up-left')],
							title: active_call ? _('Prohodit s probíhajícím hovorem') : _('Vrátit do probíhajícího hovoru'),
							'on:click': ev => {
								Office.CallManager.unpark(call);
							},
						}]
					}, {
						className: 'header',
						children: [{
							className: 'number',
							textContent: call.caller,
						}]
					}, {
						className: 'info',
						textContent: contact ? [contact.name, contact.company].filter(v => v).join(', ') : '',
					}, {
						':skip': !call.queue,
						className: 'queue',
						textContent: _('Fronta') + ' ' + call.queue,
					}],
				};
			}),
			...held_calls.map(c => {
				let peer = c.peer || {
					number: '???',
				};

				return {
					className: 'call',
					children: [{
						nodeName: 'button',
						className: 'secondary small icon-only controls',
						children: [active_call ? Utils.useIcon('swap') : Utils.useIcon('fi-corner-up-left')],
						title: active_call ? _('Prohodit s probíhajícím hovorem') : _('Vrátit do probíhajícího hovoru'),
						'on:click': ev => {
							Office.CallManager.returnFromHold(c);
						},
					}, {
						className: 'header',
						children: [{
							className: 'number',
							textContent: peer.number,
						}]
					}, {
						className: 'info',
						textContent: [peer.name, peer.company].filter(v => v).join(', '),
					}, {
						':skip': !c.queue,
						className: 'queue',
						textContent: _('Fronta') + ' ' + c.queue,
					}],
				};
			})
		]);

		this.heldAndParkedCallsCt.classList.toggle('hidden', parked_calls.calls.length + held_calls.length == 0);
	},

	renderCallRingingMuteButton() {
		let el = this.ringingMuteEl;
		let muted = Office.CallManager.isRingingMuted();
		
		el.className = 'ring-mute small icon-only ' + (muted ? 'error' : 'secondary');
		el.title = muted ? _('Zapnout vyzvánění') : _('Vypnout vyzvánění');
		el.gwReplaceChildren([muted ? Utils.useIcon('notificationsoff') : Utils.useIcon('notifications')])
	},

	setData(data) {
		let is_held_here = c => (c.status == 'hold' && !c.transfer) && c.webphone_session;

		this.extensionEl.textContent = data.exten == null ? _('Žádná linka') : data.exten;
		this.extensionStatus.dataset.status = data.exten_status;

		let devices_key = JSON.stringify(data.devices);
		if (devices_key != this.current_devices_key) {
			this.deviceSwitcher.setItems(data.devices);
			this.current_devices_key = devices_key;
		}

		if (data.selected_device) {
			this.deviceSwitcher.setSelected(data.selected_device.id);

			this.deviceStatus.textContent = data.selected_device.error_message || '';
			this.deviceStatus.gwHide(!data.selected_device.error_message);
		} else {
			this.deviceStatus.textContent = _('Není k dispozici žádné zařízení');
			this.deviceStatus.gwHide(false);
		}

		//this.headerEl.dataset.anim = data.active_call ? 'calling' : 'ringing';

		if (data.active_call) {
			this.renderCall(data.active_call);
		} else {
			this.renderDisconnectedState();
		}

		this.callActionsGlobal.el.gwHide(!!data.active_call);
		this.callActionsGlobal.refreshDialer();
		
		this.held_calls = data.calls.filter(c => is_held_here(c));
		this.renderHeldAndParkedCalls();

		this.renderWaitingCalls(data.calls.filter(c => !is_held_here(c) && c !== data.active_call));

		this.renderCallRingingMuteButton();

		if (this.debugger) {
			this.debugger.pushCall(data.active_call);
		}
	},

	onDeviceSelect(dev) {
		Office.CallManager.setActiveDevice(dev);
	},

	onBusMessage(msg) {
		if (msg.type == 'CALL_MANAGER_CHANGE') {
			this.setData(Office.CallManager.getState());
		}

		if (msg.type === 'PARKED_CALLS_CHANGED') {
			this.parked_calls = msg.data;
			this.renderHeldAndParkedCalls();
		}
	},
});

// }}}
// {{{ Office.Debugger

/*
 * Realtime state debugger displays:
 *
 * - history of deep backend states as fetched from the server
 *   before projection
 * - projected dialer state for the extension
 * - status of the webphone sessions at each point
 * - option to save the captured session
 * - ability to load the list of ami events for a given list of
 *   linekd_ids (or a time range?)
 */
GW.define('Office.Debugger', 'GW.Component', {
	initComponent() {
		this.el = document.gwCreateElement({
			children: [{
				ref: 'controls',
				className: 'controls',
				children: [{
					nodeName: 'button',
					textContent: 'Save',
					'on:click': ev => {
						let trace = [];
						for (let el of this.trace.querySelectorAll('.call-state')) {
							trace.push(el.$data);
						}

						trace.reverse();

						let file = new File([JSON.stringify(trace, null, '    ')], "trace.json", {
							type: "text/plain",
						});

						let url = URL.createObjectURL(file);
						let a = document.createElement('a');
						a.href = url;
						a.download = '';
						a.click();
						URL.revokeObjectURL(url);
					},
				}, {
					nodeName: 'button',
					textContent: 'Load',
					'on:click': async ev => {
						let f = await Uploads.fileSelect('*.json');
						let text = await f.text();
						let json = JSON.parse(text);
						
						this.trace.textContent = '';
						for (let t of json) {
							if (t.call && t.call.webphone_session) {
								t.call.webphone_session.isOnHold = function() {
									return this.on_hold;
								};

								t.call.webphone_session.isMuted = function() {
									return this.muted;
								};
							}

							this.renderItem(t);
						}
					},
				}, {
					nodeName: 'button',
					textContent: 'Clear',
					'on:click': ev => {
						this.trace.textContent = '';
						this.lastCall = null;
					},
				}],
			}, {
				ref: 'trace',
				className: 'trace',
			}],
		}, this);

		this.lastCall = null;
		this.timeFmt = new Intl.DateTimeFormat('cs-CZ', {
			year: 'numeric', month: 'numeric', day: 'numeric',
			hour: 'numeric', minute: 'numeric', second: 'numeric',
		});
	},

	formatTime(ts) {
		if (ts == null) {
			return '';
		}

		if (!(ts instanceof Date)) {
			ts = new Date(ts);
		}

		return this.timeFmt.format(ts) + '.' + ts.getMilliseconds();
	},

	renderCall(data) {
		if (data.call == null) {
			return document.gwCreateElement({
				className: 'call-state',
				$data: data,
				children: [{
					className: 'header',
					children: [{
						className: 'time',
						textContent: this.formatTime(data.ts),
					}, {
						className: 'linkedid',
						textContent: 'no call',
					}],
				}],
			});
		}

		let orig_call = data.call;
		let dbc = data.call.debug ? data.call.debug.dbc : null;
		let call = {...data.call};
                delete call.debug;

		let session_id = call.webphone_session_id;
		let webphone_id = call.webphone;

		function get_peer_conns(p) {
			if (!dbc) {
				return '';
			}

			return dbc.connections.map((c, idx) => {
				if (c.channel_caller && c.channel_caller.channel == p.channel) {
					return idx + 1;
				}

				if (c.channel_callee && c.channel_callee.channel == p.channel) {
					return idx + 1;
				}
			}).filter(v => v).join(', ');
		}

		let callControl = new Office.CallControl();
		callControl.setData(call);

		return document.gwCreateElement({
			className: 'call-state',
			$data: data,
			children: [{
				className: 'header',
				children: [{
					className: 'time',
					textContent: this.formatTime(data.ts),
				}, {
					className: 'linkedid',
					textContent: call.call_id + (webphone_id ? ' / ' + 'webphone ' + webphone_id : '') + (session_id ? ' / session ' + session_id : ''),
				}],
			}, {
				nodeName: 'table',
				children: [{
					nodeName: 'tr',
					children: [{
						nodeName: 'th',
						textContent: 'First time',
					}, {
						nodeName: 'th',
						textContent: 'Chan',
					}, {
						nodeName: 'th',
						textContent: 'Status',
					}, {
						nodeName: 'th',
						textContent: 'Caller',
					}, {
						nodeName: 'th',
						textContent: 'Conn line',
					}, {
						nodeName: 'th',
						textContent: 'Queue',
					}, {
						nodeName: 'th',
						textContent: 'Conns',
					}]
				}, ...((dbc && dbc.peers) || []).map(p => {
					return {
						nodeName: 'tr',
						children: [{
							nodeName: 'td',
							textContent: this.formatTime(p.first_time),
						}, {
							nodeName: 'td',
							textContent: p.channel,
						}, {
							nodeName: 'td',
							'attr:data-state': p.state,
							textContent: p.state + (p.hold ? ' hold' : '') + (p.musichold ? ' musichold' : '') + (p.atxfer ? ' atxfer' : ''),
						}, {
							nodeName: 'td',
							className: p.caller == call.my_exten ? 'self' : undefined,
							textContent: p.caller,
						}, {
							nodeName: 'td',
							className: p.connected_line == call.my_exten ? 'self' : undefined,
							textContent: p.connected_line,
						}, {
							nodeName: 'td',
							textContent: p.queue,
						}, {
							nodeName: 'td',
							textContent: get_peer_conns(p),
						}],
					};
				})],
			}, {
				nodeName: 'table',
				children: [{
					nodeName: 'tr',
					children: [{
						nodeName: 'th',
						textContent: 'Conn',
					}, {
						nodeName: 'th',
						textContent: 'Dial',
					}, {
						nodeName: 'th',
						textContent: 'Ring',
					}, {
						nodeName: 'th',
						textContent: 'Start',
					}, {
						nodeName: 'th',
						textContent: 'Status',
					}, {
						nodeName: 'th',
						textContent: 'Caller',
					}, {
						nodeName: 'th',
						textContent: 'Callee',
					}, {
						nodeName: 'th',
						textContent: 'Queue',
					}]
				}, ...((dbc && dbc.connections) || []).map((c, idx) => {
					return {
						nodeName: 'tr',
						children: [{
							nodeName: 'td',
							textContent: idx + 1,
						}, {
							nodeName: 'td',
							textContent: this.formatTime(c.dial_ts),
						}, {
							nodeName: 'td',
							textContent: this.formatTime(c.ring_ts),
						}, {
							nodeName: 'td',
							textContent: this.formatTime(c.start_ts),
						}, {
							nodeName: 'td',
							textContent: c.external || '',
						}, {
							nodeName: 'td',
							className: c.caller == call.my_exten ? 'self' : undefined,
							children: [{
								className: 'name',
								textContent: c.caller,
							}, {
								className: 'channel',
								textContent: c.channel_caller ? c.channel_caller.channel : '',
							}, {
								className: 'state',
								'attr:data-state': c.channel_caller ? c.channel_caller.state : '',
								textContent: c.channel_caller ? c.channel_caller.state : '',
							}],
						}, {
							nodeName: 'td',
							className: c.callee == call.my_exten ? 'self' : undefined,
							children: [{
								className: 'name',
								textContent: c.callee,
							}, {
								className: 'channel',
								textContent: c.channel_callee ? c.channel_callee.channel : '',
							}, {
								className: 'state',
								'attr:data-state': c.channel_callee ? c.channel_callee.state : '',
								textContent: c.channel_callee ? c.channel_callee.state : '',
							}],
						}, {
							nodeName: 'td',
							textContent: c.queue,
						}],
					};
				})],
			}, {
				className: 'dialer-state',
				children: [{
					textContent: 'dialer shows ' + JSON.stringify(call, null, '    '),
				}, callControl.el],
			}],
		});
	},

	renderWebphone(data) {
		let ev = {...data.ev};
		let children = [];
		let session_id = ev.session;
		let webphone_id = ev.webphone;

		delete ev.session;
		delete ev.webphone;

		if (ev.msg == 'signaling') {
			children = [{
				className: 'sdp',
				children: [{
					className: 'sdp-head',
					textContent: 'local' + (ev.local ? ' ' + ev.local.type : ''),
				}, {
					className: 'sdp-body',
					textContent: ev.local ? ev.local.sdp : '---',
				}],
			}, {
				className: 'sdp',
				children: [{
					className: 'sdp-head',
					textContent: 'remote' + (ev.remote ? ' ' + ev.remote.type : ''),
				}, {
					className: 'sdp-body',
					textContent: ev.remote ? ev.remote.sdp : '---',
				}],
			}];
		} else if (ev.stats) {
			let stats = ev.stats;
			ev.stats = stats.filter(s => !['local-candidate', 'remote-candidate', 'candidate-pair'].includes(s.type));

			children = [{
				textContent: JSON.stringify(ev, null, '    '),
			}, {
				nodeName: 'table',
				children: [{
					nodeName: 'tr',
					children: [{
						nodeName: 'th',
						textContent: 'Timestamp',
					}, {
						nodeName: 'th',
						textContent: 'Sent/Recvd',
					}, {
						nodeName: 'th',
						textContent: 'Priority',
					}, {
						nodeName: 'th',
						textContent: 'Local / Remote',
					}, {
						nodeName: 'th',
						textContent: 'State',
					}, {
						nodeName: 'th',
						textContent: 'Nominated',
					}, {
						nodeName: 'th',
						textContent: 'Selected',
					}]
				}, ...stats.filter(s => s.type == 'candidate-pair').map(s => {
					return {
						nodeName: 'tr',
						'attr:data-cstate': s.state,
						children: [{
							nodeName: 'td',
							textContent: this.formatTime(s.timestamp),
						}, {
							nodeName: 'td',
							textContent: s.bytesSent + ' / ' + s.bytesReceived,
						}, {
							nodeName: 'td',
							textContent: s.priority,
						}, {
							nodeName: 'td',
							textContent: s.localCandidateId + ':' + s.remoteCandidateId,
						}, {
							nodeName: 'td',
							textContent: s.state,
						}, {
							nodeName: 'td',
							textContent: s.nominated ? 'yes' : 'no',
						}, {
							nodeName: 'td',
							textContent: s.selected ? 'yes' : 'no',
						}],
					};
				})],
			}, {
				nodeName: 'table',
				children: [{
					nodeName: 'tr',
					children: [{
						nodeName: 'th',
						textContent: 'ID',
					}, {
						nodeName: 'th',
						textContent: 'Timestamp',
					}, {
						nodeName: 'th',
						textContent: 'Type',
					}, {
						nodeName: 'th',
						textContent: 'Priority',
					}, {
						nodeName: 'th',
						textContent: 'Address',
					}, {
						nodeName: 'th',
						textContent: 'Protocol',
					}, {
						nodeName: 'th',
						textContent: 'C. Type',
					}]
				}, ...stats.filter(s => s.type == 'local-candidate' || s.type == 'remote-candidate').map(s => {
					return {
						nodeName: 'tr',
						children: [{
							nodeName: 'td',
							textContent: s.id,
						}, {
							nodeName: 'td',
							textContent: this.formatTime(s.timestamp),
						}, {
							nodeName: 'td',
							textContent: s.type,
						}, {
							nodeName: 'td',
							textContent: s.priority,
						}, {
							nodeName: 'td',
							textContent: s.address + ':' + s.port,
						}, {
							nodeName: 'td',
							textContent: s.protocol,
						}, {
							nodeName: 'td',
							textContent: s.candidateType,
						}],
					};
				})],
			}];
		} else {
			children = [JSON.stringify(ev, null, '    ')];
		}

		return document.gwCreateElement({
			className: 'call-state',
			$data: data,
			children: [{
				className: 'header',
				children: [{
					className: 'time',
					textContent: this.formatTime(data.ts),
				}, {
					className: 'linkedid',
					textContent: 'webphone ' + webphone_id + (session_id ? ' / session ' + session_id : ''),
				}],
			}, {
				className: 'dialer-state',
				children,
			}],
		});
	},

	renderItem(data) {
		if (data.type == 'call-state') {
			this.trace.prepend(this.renderCall(data));
		} else if (data.type == 'webphone') {
			this.trace.prepend(this.renderWebphone(data));
		}
	},

	pushCall(call) {
		let session_exten = Application.session.user.extension;

		call = call || null;

		if (call) {
			if (call.webphone_session) {
				call = {
					...call,
					webphone: call.webphone_session.__webphone.id,
					webphone_exten: call.webphone_session.__webphone.extension,
					webphone_session_id: call.webphone_session._id,
					webphone_session: {
						on_hold: call.webphone_session.isOnHold(),
						muted: call.webphone_session.isMuted(),

						isOnHold() {
							return this.on_hold;
						},

						isMuted() {
							return this.muted;
						},
					},
					session_exten,
				};
			} else {
				call = {
					...call,
					session_exten,
				};
			}
		}

		if (JSON.stringify(this.lastCall) == JSON.stringify(call)) {
			return;
		}

		this.lastCall = call;
		this.renderItem({
			type: 'call-state',
			ts: new Date,
			call,
		});
	},
});

// }}}
// {{{ Office.LastContactSuggestionsPopup

GW.define('Office.LastContactSuggestionsPopup', 'SuggestionsPopup', {
	searchContacts: false,
	lastContactsApi: 'contacts/last-called',

	'after:initComponent'() {
		this.el.classList.add('dialer-input-suggestions');
		this.el.gwReplaceChildren([{
			className: 'title',
			textContent: _('Poslední hovory'),
		}, {
			ref: 'list',
		}], this);
	},

	renderItem(c) {
		return {
			$value: c.number,
			nodeName: 'a',
			href: '#',
			className: 'suggestion selectable',
			children: [{
				className: 'number',
				textContent: c.number,
			}, {
				className: 'info',
				textContent: [c.name, c.company].filter(v => v).join(', '),
			}],
		};
	},

	async loadData(query) {
		let signal = Utils.abortAndRetry(this, 'suggest');
		let match = TextUtils.createMatchAll(query || '');
                let contacts = [];

		if (query && this.searchContacts) {
			contacts = await REST.GET('contacts', {limit: 30, q: query}, {signal});

			contacts = contacts.flatMap(c => (c.phones || []).map(phone => ({
				number: phone,
				name: c.name,
				company: c.company,
			})));
		} else {
			[contacts] = await Utils.cache(this, 'last-called', 60000, REST.GET, this.lastContactsApi, null, {signal});
		}

		contacts = contacts.filter(v => match(v.number + ' ' + (v.company || '') + ' ' + (v.name || '')));

		this.list.gwReplaceChildren(contacts.map(c => this.renderItem(c)));

		return contacts.length > 0;
	},
});

// }}}

// Call history:

// {{{ Office.CallList

Office.renderDirectionIcon = function(call, useCDRcolors) {
	const directionText = Formats.call_direction(call.direction);
	const connectedText = Formats.call_result(call.result);
	const icons = {
		'in': 'phone-incoming-filled',
		'out': 'phone-outgoing-filled',
		'internal': 'phone-internal-filled',
	};
	const color = call.result === 'connected' ? ( call.direction === "in" && !useCDRcolors ? 'info' : 'success') : 'error';
	return {
		className: 'icon-wrapper',
		title: directionText + ' ' + connectedText.toLowerCase(),
		children: [
			Utils.useIcon(icons[call.direction], {cls: color})
		],
	};
}

GW.define('Office.CallList', 'GW.Component', {
	initComponent() {
		this.el = document.gwCreateElement({
			className: 'js-msgbus',
		}, this);
	},

	renderPhoneNumber(ct, number) {
		ct.textContent = number || '';
	},

	renderParty(party) {
		let numberEl = document.gwCreateElement({
			className: 'number',
		});

		this.renderPhoneNumber(numberEl, party.number);

		return [numberEl, {
			className: 'info',
			children: [{
				className: 'name',
				textContent: party.name || '',
			}, {
				className: 'company',
				textContent: party.company || '',
			}, {
				className: 'group',
				textContent: party.group_name || '',
			}],
		}];
	},

	renderCall(call) {
		/*
		 * Show various peers depending on where the logged in
		 * user is in the chain:
		 *
		 * [from] -> me -> last_callee
		 * [from] -> me
		 * [from] -> [callee] -> me
		 * me -> [callee] -> [last_callee]
		 * me -> [callee]
		 */
		let my_id = Application.session.user.id;
		let my_number = Application.session.user.extension;
		let is_forwarded = call.last_callee && call.last_callee.number && call.last_callee.number != call.callee.number;
		let peers = [call.caller];
		if (call.caller.user_id == my_id) {
			if (call.callee.number == null) {
				// this may happen for queue calls that are
				// hanged up before being picked pu by queue
				// member
				if (call.queue) {
					peers = [{
						number: call.queue,
						name: call.queue_name,
					}];
				}
			} else {
				peers = [call.callee];

				if (is_forwarded) {
					peers.push(call.last_callee);
				}
			}
		} else if (is_forwarded && call.last_callee.user_id == my_id) {
			peers.push(call.callee);
		}

		let chain = [{queue: call.queue, name: call.queue_name}, call.caller, call.callee, call.last_callee];

		return document.gwCreateElement({
			$data: call,
			className: 'call',
			'attr:data-call': JSON.stringify(chain),
			'attr:data-call-direction': call.direction, // in/out/internal
			'attr:data-call-result': call.result, // connected/rejected/abandoned/missed/unrealized
			children: [
				Office.renderDirectionIcon(call)
			, {
				className: 'party',
				children: this.renderParty(peers[0]),
			}, {
				':skip': !peers[1],
				className: 'arrow',
			}, {
				':skip': !peers[1],
				className: 'party',
				children: peers[1] && this.renderParty(peers[1]),
			}, {
				className: 'time',
				textContent: Formats.datetime(call.start),
			}, {
				className: 'controls',
				children: [{
					nodeName: 'button',
					type: 'button',
					className: 'secondary icon-only small' + (!call.has_recordings ? ' vhidden' : ''), // When history entry has no recordings, this button is used as a non-visual padding element
					children: [Utils.useIcon('dialingfilled')],
					title: call.recordings_readable ? _('Zobrazit nahrávky') : _('K nahrávkám nemáte přístup'),
					'on:click': ev => this.showRecordings(call),
					disabled: !call.recordings_readable
				},{
					nodeName: 'button',
					type: 'button',
					className: 'success icon-only small',
					'attr:data-action': 'dial',
					title: _('Vytočit'),
					'on:click': async ev => {
						let number = (await Office.CallManager.selectContact(ev, peers)).number;
						if (call.direction == 'in') {
							number = await Office.QueueManager.makeQueueAssignmentNumber(call.queue, number);
						}
						Office.CallManager.doAction({action: 'dial', number});
					},
				}],
			}],
		});
	},

	async showRecordings(call) {
		let recordings = await REST.GET('calls/' + encodeURIComponent(call.id) + '/recordings');

		let dlg = new CDR.CallRecordingsDialog({
			recordingsBaseUrl: API_BASE + 'calls/' + encodeURIComponent(call.id) + '/recordings/',
		});

		dlg.setRecordings(recordings);
	},

	setData(list) {
		if (list.length == 0) {
			this.el.gwReplaceChildren([{
				className: 'empty',
				textContent: _('Žádné hovory neproběhly'),
			}]);
			return;
		}

		this.el.gwReplaceChildren(list.map(this.renderCall.bind(this)));
		this.refreshControls();
	},

	refreshControls() {
		for (let btn of this.el.querySelectorAll('button[data-action]')) {
			btn.disabled = !Office.CallManager.canDo(btn.dataset.action);
		}
	},

	onBusMessage(msg) {
		if (msg.type == 'CALL_MANAGER_CHANGE') {
			this.refreshControls();
		}
	},
});

// }}}
// {{{ Office.HistoryPanel

GW.define('Office.HistoryPanel', 'GW.Component', {
	buttonSwitcherExtraClass: '',
	
	initComponent() {
		this.loaded_calls = [];

		this.el = document.gwCreateElement({
			className: 'js-msgbus',
			children: [{
				className: 'toolbar',
				children: [{
					className: "toolbar-top",
					children: [{
						ref: 'tabs',
						xtype: 'Widgets.ButtonSwitcher',
						cls: "button-switcher small " + this.buttonSwitcherExtraClass,
						'on:beforeselect': (c, i) => {
							Application.route({
								paramsFn: params => {
									params.set('history', i.id);
									return params;
								},
							});
						},
					}]
				}]
			}, {
				ref: 'cards',
				xtype: 'Widgets.CardSwitcher',
			}],
		}, this);
	},

	renderPhoneNumber(ct, number) {
		ct.textContent = number || '';
	},

	getTabs() {
		return [{
			id: 'all',
			title: _('Všechny'),
			query: {},
		}, {
			id: 'missed',
			title: _('Zmeškané'),
			query: {
				result: 'not-connected',
			},
		}];
	},

	getListComponentConfig() {
		return {
			xtype: 'Office.CallList',
			renderPhoneNumber: this.renderPhoneNumber,
		};
	},

	async applyParams(params) {
		let tabs = this.getTabs();

		this.tabs.setItems(tabs);

		let tabId = params.get('history');
		let tab = tabs.find(t => t.id == tabId);
		if (!tab) {
			tab = tabs[0];
		}

		this.tabs.setSelected(tab.id);

		let c = await this.cards.show({
			id: 'calls',
			...this.getListComponentConfig(),
		});

		let signal = Utils.abortAndRetry(this, 'reload');
		let [calls, callsChanged] = await Utils.cache(this, 'calls', 60000, REST.GET, 'calls', {
			...this.getApiQuery(),
			...tab.query,
			limit: 200,
		}, {signal});

		if (callsChanged) {
			c.setData(calls);
			this.loaded_calls = calls;
		}
	},

	hasCallLoaded(call_id) {
		return this.loaded_calls.some(c => c.id == call_id);
	},

	async reload() {
		await this.applyParams(new URLSearchParams(location.search));
	},
	
	getApiQuery() {
		return {
			user_id: Application.session.user.id,
		};
	},

	onBusMessage(msg) {
		if (msg.type == 'CALL_ENDED') {
			// reload data after call ends
			Utils.clearCache(this);

			this.reload();
		}
	},
});

// }}}

// Contact directory:

// {{{ Office.Office.ContactEditDialog

GW.define('Office.ContactEditDialog', 'Main.FormDialog', {

	title: _('Úprava kontaktu'),
	disableSubmitOnInvalidForm: true,

	// form data, if id is set, REST.PUT is called to edit the contact, otherwise REST.POST to create a new contact
	contact: null,

	// possible preloaded contact groups
	contactGroups: null, 
	
	showDeleteButton: true,
	disabledFields: [],

	'after:initComponent'() {
		this.el.classList.add('contact-edit-dialog')
		if (this.contact) {
			this.setFormFieldsData({
				...this.contact,
				phone0: (this.contact.phones || [])[0],
				phone1: (this.contact.phones || [])[1],
				group: this.contact.group_id
			});
		}
	},

	renderFormFields() {
		return [{
			xtype: 'SelectField',
			name: 'group',
			ref: 'contactGroupSelect',
			async getOptions({signal}) {
				const groups = this.contactGroups || await REST.GET('contact-groups', null, {signal})
				return groups.filter(g => g.editable).map(g => ({
					value: g.id,
					text: g.name
				}));
			},
			label: _('Adresář'),
		}, {
			xtype: 'TextField',
			name: 'name',
			optional: true,
			label: _('Jméno'),
		}, {
			xtype: 'TextField',
			name: 'company',
			optional: true,
			label: _('Firma'),
		}, {
			xtype: 'TextField',
			name: 'phone0',
			optional: true,
			label: _('Telefon 1'),
		}, {
			xtype: 'TextField',
			name: 'phone1',
			optional: true,
			label: _('Telefon 2'),
		}, {
			xtype: 'TextField',
			name: 'email',
			optional: true,
			label: _('E-mail'),
		}, {
			xtype: 'TextareaField',
			name: 'note',
			optional: true,
			label: _('Poznámka'),
		}].map(i => this.disabledFields.includes(i.name) ? {...i, disabled: true} : i);
	},
	
	renderFormButtons() {
		return [{
			':skip': !(this.contact && this.contact.id && this.showDeleteButton),
			ref: 'deleteButton',
			nodeName: 'button',
			type: 'button',
			className: 'error-secondary icon-left',
			children: [
				Utils.useIcon('delete'),
				{
					textContent: _('Odstranit'),
				}
			],
			'on:click': async ev => {
				await REST.DELETE('contacts/' + this.contact.id);
				Application.notify({
					kind: 'success',
					exclusive: 'contact-update',
					text: _('Kontakt odstraněn'),
					timeout: 3000,
				});
				this.close();
			},
		}, {
			ref: 'buttonConfirm',
			nodeName: 'button',
			type: 'submit',
			className: 'primary',
			textContent: _('Uložit'),
		}];
	},

	async onSave(data) {
		let res;
		if (this.contact && this.contact.id) {
			res = data;
			await REST.PUT('contacts/' + this.contact.id, data);
			Application.notify({
				kind: 'success',
				exclusive: 'contact-update',
				text: _('Kontakt upraven'),
				timeout: 3000,
			});
		} else {
			res = await REST.POST('contacts', data);
			Application.notify({
				kind: 'success',
				exclusive: 'contact-update',
				text: _('Kontakt vytvořen'),
				timeout: 3000,
			});
		}
		return res;
	},
});

// }}}
// {{{ Office.ContactsList

GW.define('Office.ContactsList', 'SmartTable', {
	monitorOverflow: true,
	showEditButton: false,

	'after:initComponent'() {
		this.el.classList.add('js-msgbus');
		this.el.addEventListener('click', async ev => {
			let btn = ev.target.closest('[data-action]');
			if (btn) {
				let tr = ev.target.closest('tr');
				
				if (btn.dataset.action === 'edit') {
					new Office.ContactEditDialog({
						contact: tr.$data
					});
					return;
				}
				
				let peers = (tr.$data.phones || []).filter(v => v).map(number => ({number}));
				let number = (await Office.CallManager.selectContact(ev, peers)).number;

				switch (btn.dataset.action) {
				case 'dial':
				case 'blind-transfer':
				case 'attended-transfer':
					Office.CallManager.doAction({action: btn.dataset.action, number});
					break;
				case 'sms':
					new Office.SmsSendDialog({
						parentElement: this.el,
						to: number
					});
					break;
				}
			}
		}, true);
	},

	renderPhoneNumber(ct, number) {
		ct.textContent = number || '';
	},

	getColumns() {
		return [{
			id: 'name',
			name: _('Jméno'),
		}, {
			id: 'company',
			name: _('Firma'),
		}, {
			id: 'phone0',
			name: _('Telefon 1'),
			formatCell: (c, n, r) => this.renderPhoneNumber(c, r.phones[0]),
		}, {
			id: 'phone1',
			name: _('Telefon 2'),
			formatCell: (c, n, r) => this.renderPhoneNumber(c, r.phones[1]),
		}, {
			id: 'email',
			name: _('E-mail'),
			formatCell(td, v, row) {
				if (!v) {
					return;
				}

				td.gwCreateChild({
					nodeName: 'a',
					href: 'mailto:' + v,
					textContent: v
				});
			},
		}, {
			id: 'note',
			name: _('Poznámka'),
		}, {
			id: 'ctl',
			name: _('Akce'),
			formatCell: (td, v, row) => {
				if ((row.phones || []).length == 0) {
					return;
				}

				td.gwCreateChild({
					nodeName: 'button',
					className: 'success icon-only small',
					'attr:data-action': 'dial',
					title: _('Vytočit'),
				});

				td.gwCreateChild({
					nodeName: 'button',
					className: 'success icon-only small',
					'attr:data-action': 'blind-transfer',
					title: _('Přepojit'),
				});

				td.gwCreateChild({
					nodeName: 'button',
					className: 'success icon-only small',
					'attr:data-action': 'attended-transfer',
					title: _('Přepojit s odložením'),
				});
				
				if (env.SMS_ACCOUNTS && env.SMS_ACCOUNTS.length > 0) {
					td.gwCreateChild({
						nodeName: 'button',
						className: 'success icon-only small',
						'attr:data-action': 'sms',
						title: _('Odeslat SMS'),
					});
				}
				
				if (this.showEditButton) {
					td.gwCreateChild({
						nodeName: 'button',
						className: 'secondary icon-only small',
						'attr:data-action': 'edit',
						title: _('Upravit'),
					});
				}
			},
		}];
	},

	'after:setData'() {
		this.refreshControls();
	},

	refreshControls() {
		for (let btn of this.body.querySelectorAll('button[data-action]')) {
			btn.disabled = !['sms', 'edit'].includes(btn.dataset.action) && !Office.CallManager.canDo(btn.dataset.action);
		}
	},

	onBusMessage(msg) {
		if (msg.type == 'CALL_MANAGER_CHANGE') {
			this.refreshControls();
		}
	},
});

// }}}
// {{{ Office.DirectoryPanel

GW.define('Office.DirectoryPanel', 'GW.Component', {
	initComponent() {
		this.el = document.gwCreateElement({
			className: 'js-msgbus',
			socketSubscriptions: ['contacts'],
			children: [{
				className: 'toolbar wrap',
				children: [{
					ref: 'tabs',
					xtype: 'Widgets.ButtonSwitcher',
					'on:beforeselect': (c, i) => {
						Application.route({
							paramsFn: params => {
								params.set('directory', i.id);
								return params;
							},
						});
					},
				}, {
					ref: 'searchEl',
					nodeName: 'input',
					type: 'search',
					placeholder: _('Hledat jméno, firmu, telefon'),
					className: 'small',
					'on:input': ev => {
						Utils.dedupeCalls(this, 'setQuery', 300, () => {
							Application.route({
								paramsFn: params => {
									params.set('q', ev.target.value);
									return params;
								},
							});
						});
					},
				}, {
					nodeName: 'button',
					ref: 'createContactButton',
					type: 'button',
					className: 'primary small icon-left hidden',
					children: [Utils.useIcon('plus'), _('Přidat kontakt')],
					'on:click': () => new Office.ContactEditDialog({
						contactGroups: this.groups.filter(g => g.editable),
						title: _('Nový kontakt'),
					}),
				}],
			}, {
				ref: 'cards',
				xtype: 'Widgets.CardSwitcher',
			}],
		}, this);
	},

	renderPhoneNumber(ct, number) {
		ct.textContent = number || '';
	},

	async applyParams(params) {
		let signal = Utils.abortAndRetry(this, 'reload');
		let [groups, groupsChanged] = await Utils.cache(this, 'groups', 60000, REST.GET, 'contact-groups', null, {signal});
		if (groupsChanged) {
			this.groups = groups;
			this.tabs.setItems(groups.map(g => {
				return {
					id: g.id,
					title: g.name,
				};
			}));
			
			const editableGroups = groups.filter(g => g.editable);
			this.createContactButton.classList.toggle('hidden', editableGroups.length == 0);
		}

		let groupId = params.get('directory');
		let group = groups.find(g => g.id == groupId);
		if (!group && groups[0]) {
			group = groups[0];
			params.set('directory', group.id);
		}

		if (group) {
			this.tabs.setSelected(group.id);
		}

		let q = params.get('q') || '';
		if (this.searchEl.value != q) {
			this.searchEl.value = q;
		}

		await this.reloadContacts({q, signal, groups: 'all'});

		if (!Application.isMobileUI()) {
			this.searchEl.focus();
		}
	},
	
	async loadGroupsContacts({q, groups, signal}) {
		return await Promise.all(groups.map(async id => ({
			group_id: id,
			contacts: await REST.GET('contacts', {
				group: id,
				limit: 150,
				q
			}, {signal}),
		})));
	},
	
	async reloadContacts({q, signal, force}) {
		
		const query = (typeof q === 'string') ? q : this.lastQuery;
		
		if (force) {
			Utils.clearCache(this, 'contacts');
		}

		let [groupContacts, changed] = await Utils.cache(this, 'contacts', 60000, this.loadGroupsContacts.bind(this), {
			q: query,
			groups: this.groups.map(g => g.id),
			signal: signal || Utils.abortAndRetry(this, 'reload'),
		});

		const nonEmptyGroups = groupContacts.filter(gc => gc.contacts.length);

		if (changed || this.lastQuery != q) {
			this.lastQuery = q;
			
			this.tabs.setItems(nonEmptyGroups.map(gc => {
				var group = this.groups.find(g => g.id == gc.group_id);
				return {
					id: group.id,
					title: group.name,
				};
			}));
		}

		if (nonEmptyGroups.length) {
			let selected = this.tabs.getSelected();
			if (!selected) {
				// selected tab not amongst visible tabs with contacts, update selected:
				Application.route({
					paramsFn: params => {
						params.set('directory', nonEmptyGroups[0].group_id);
						return params;
					},
				});
				return; // component will be refreshed, abort processing
			}

			const card = await this.cards.show({
				id: 'contacts',
				xtype: 'Office.ContactsList',
				renderPhoneNumber: this.renderPhoneNumber,
			});

			card.showEditButton = (this.groups.find(g => g.id == selected) || {}).editable;
			card.setData(groupContacts.find(gc => gc.group_id == selected).contacts);
		} else {
			this.cards.show({
				id: 'empty',
				xtype: 'GW.Component',
				initComponent() {
					this.el = document.gwCreateElement({
						className: 'empty',
						textContent: query ? _('Nebyly nalezeny žádné kontakty') : _('Nejsou založeny žádné adresáře'),
					});
				},
			})
		}
	},

	onBusMessage(msg) {
		if (msg.type == 'CONTACT_CHANGED') {
			if ((msg.contact_group_ids || []).includes(this.tabs.getSelected())) {
				this.reloadContacts({force: true, groups: msg.contact_group_ids});
			}
		}
	},
});

// }}}
// {{{ Office.VoicemailsTable

GW.define('Office.VoicemailsTable', 'SmartTable', {
	emptyText: _('Žádné záznamy k dispozici'),

	getColumns() {
		return [{
			id: 'mailbox',
			name: _('Záznamník'),
		}, {
			id: 'start_ts',
			name: _('Čas záznamu'),
			format: 'datetime',
		}, {
			id: 'queue',
			name: _('Fronta'),
		}, {
			id: 'caller_number',
			name: _('Volající'),
		}, {
			id: 'caller_name',
			name: _('Jméno'),
		}, {
			id: 'caller_company',
			name: _('Firma'),
		}, {
			id: 'duration',
			name: _('Délka záznamu'),
			format: 'duration_unpadded',
		}, {
			id: 'ctl',
			name: _('Akce'),
			formatCell: (td, v, row) => {

				if (row.readable) {

					const url = API_BASE + 'voicemails/' + row.voicemail_id + '?active_user_group=' + Application.getActiveUserGroup().id;
					
					td.gwCreateChild({
						className: 'voicemail-btns',
						children: [{
							xtype: 'AudioPlayerButton',
							audioUrl: url
						}, {
							nodeName: 'a',
							className: 'button small secondary icon-left btn-voicemail-download',
							children: [Utils.useIcon('download'), _('Stáhnout')],
							href: url + '&download=1',
							download: '',
						}]
					})
				} else {
					td.gwCreateChild({
						nodeName: 'button',
						className: 'secondary icon-only small',
						title: _('Záznam není dostupný'),
						disabled: true,
						children: [Utils.useIcon('play')],
					});
				}
			}
		}];
	},
	
	'wrap:setData'(fn, items) {
		items.forEach(item => {
			item.caller_number = item.caller.number;
			item.caller_name = item.caller.name;
			item.caller_company = item.caller.company;
		});
		fn(items);
	}
});

// {{{ Office.Mailbox
GW.define('Office.Mailbox', 'GW.Component', {
	initComponent() {
		this.el = document.gwCreateElement({
			className: 'js-msgbus',
			socketSubscriptions: ['voicemails'],
			children: [{
				className: 'toolbar wrap',
				children: [{
					ref: 'tabs',
					xtype: 'Widgets.ButtonSwitcher',
					'on:beforeselect': (c, i) => {
						Application.route({
							paramsFn: params => {
								params.set('mailbox', i.id);
								return params;
							},
						});
					},
				}],
			}, {
				ref: 'cards',
				xtype: 'Widgets.CardSwitcher',
			}],
		}, this);
	},

	async applyParams(params) {
		let signal = Utils.abortAndRetry(this, 'reload');
		let [mbs, mbsChanged] = await Utils.cache(this, 'mailboxes', 60000, REST.GET, 'mailboxes', null, {signal});
		
		let items = mbs.map(mb => ({
			id: mb.mailbox,
			title: mb.name,
		}));
		
		console.log(items);
		
		if (items.length > 1) {
			items.unshift({
				id: 'all',
				title: _('Všechny')
			});
		}
		
		if (mbsChanged) {
			this.tabs.setItems(items);
		}

		let mailbox = params.get('mailbox');
		let item = items.find(i => i.id == mailbox);
		if (!item && items[0]) {
			item = items[0];
			params.set('mailbox', item.id);
		}

		if (item) {
			this.tabs.setSelected(item.id);
		}

		await this.reloadVoicemails({signal});
	},
	
	async reloadVoicemails({signal}) {
		const mailbox = this.tabs.getSelected();
		
		if (!mailbox) {
			await this.cards.show({
				id: 'empty',
				xtype: 'GW.Component',
				initComponent() {
					this.el = document.gwCreateElement({
						className: 'empty-image-placeholder',
						children: [{
							nodeName: 'img',
							src: APP_BASE + 'img/empty-boxes.svg',
							width: "234",
							height: "190",
						}, {
							className: 'title',
							textContent: _('Nejsou k dispozici žádné záznamníky'),
						}],
					});
				},
			})
			return;
		}
		
		const items = await REST.GET('voicemails', {mailbox}, {
			signal: signal || Utils.abortAndRetry(this, 'reload')
		});
		
		const card = await this.cards.show({
			id: 'voicemails',
			xtype: 'Office.VoicemailsTable',
		});
		card.setData(items);
	},

	onBusMessage(msg) {
		if (msg.type == 'VOICEMAIL_CHANGED') {
			this.reloadVoicemails({});
		}
	},
});

// }}}

// SMS:

// {{{ Office.SmsMessageInput

GW.define('Office.SmsMessageInput', 'GW.Component', {

	charLimit: 160,
	charWarn: 150,
	
	refs: {},
	
	initComponent() {
		this.el = document.gwCreateElement({
			className: 'sms-create-message' + (!this.sendSms ? ' no-send' : ''),
			'on:click': () =>  this.refs.inputEl.focus(),
			children: [{
				nodeName: 'textarea',
				ref: 'inputEl',
				'on:keydown': e => {
					if (e.code == 'Escape' && e.target.value) { // If user already entered some input, don't let him press ESC to close the whole modal
						e.stopPropagation();
						e.preventDefault();
						return
					}
					// When Enter is pressed and messages container is scrolled to bottom - scroll it to bottom again after textarea is re-rendered (and possibly changed its height)
					if (e.code == 'Enter') {
						const messagesContainer = this.getMessagesContainer();
						if (messagesContainer && 
							Math.floor(messagesContainer.scrollTop + messagesContainer.clientHeight) >= messagesContainer.scrollHeight
						) {
							setTimeout(()=>{
								messagesContainer.scrollTop = messagesContainer.scrollHeight;
							}, 1)
						}
					}
				},
				'on:input': (e) => {
					let textareaEl = e.target;

					let showCounter = this.renderCounter(this.refs.charCounter, this.charLimit, this.charWarn, textareaEl.value);

					textareaEl.style.height = '0';
					if (textareaEl.value && (showCounter || (textareaEl.scrollHeight > 32 && (textareaEl.scrollHeight > textareaEl.clientHeight)))) {
						// Area should be expanded
						textareaEl.parentElement.classList.add('expanded');
						textareaEl.style.height = `${textareaEl.scrollHeight}px`;
					} else {
						// Area should be shrunk to its default height
						textareaEl.parentElement.classList.remove('expanded');
						textareaEl.style.removeProperty('height');
						textareaEl.removeAttribute('style');
					}

					// Toggle send button disabled state
					const sendButton = this.refs.sendButton;
					if (sendButton) {
						sendButton.disabled = !this.isSendEnabled() || !!!e.target.value;
					}
					
					this.fire('change');
				}
			}, {
				className: 'char-counter',
				ref: 'charCounter',
			}, {
				':skip': !this.sendSms,
				ref: 'sendButton',
				nodeName: 'button',
				className: 'primary icon-left',
				disabled: true,
				'on:click': async () => {
					this.refs.inputEl.disabled = true;
					this.refs.sendButton.disabled = true;
					try {
						await this.sendSms(this.refs.inputEl.value);
					} finally {
						this.refs.inputEl.value = '';
						this.refs.inputEl.disabled = false;
					}
				},
				children: [
					Utils.useIcon('send'),
					{
						textContent: _('Odeslat'),
					}
				]
			}]
		}, this.refs);
		this.refs.inputEl.focus();
		if (this.autofocus) {
			setTimeout(()=>this.refs.inputEl.focus(), 1);
		}
	},

	renderCounter: (el, limit, warn, input) => {
		let content;
		let len = input?.length || 0
		if (len >= warn) {
			content = {
				nodeName: 'span',
				className: len < limit ? 'text-muted' : 'text-error',
				textContent: `${_('Počet znaků')} ${len}/${limit}` + 
					(len >= limit ? `. ${_('Při použítí více jak {limit} znaků je možné, že SMS nebude doručena příjemci celá.'.replace('{limit}', limit))}` : ''),
			}
			el.gwReplaceChildren([content])
		}
		let result = !!content
		el.classList.toggle('visible', result)
		return result
	},
	
	async sendSms(text) {
		throw new Error("sendSms() is no implemented");
	},
	
	getMessagesContainer() {
		// to be implemented in inherited component 
		// return scrollable element with messages displayed on top of this el
		return null;
	},
	
	isSendEnabled() {
		return true;
	},
});

// }}}
// {{{ Office.SmsThreadDialog

GW.define('Office.SmsThreadDialog', 'Main.Dialog', {
	me: null,
	peer: null,
	initialMessages: null,
	loadMessages: null, // must be defined, function
	
	title: _('Historie zpráv'),
	
	'after:initComponent'() {
		this.el.classList.add('js-msgbus');
		this.el.socketSubscriptions = ['sms'];

		this.bodyEl.gwReplaceChildren([{
			className: 'thread-content-container',
			socketSubscriptions: ['sms'],
			children: [{
				className: 'thread-container',
				ref: 'messagesEl',
			}, {
				xtype: 'Office.SmsMessageInput',
				autofocus: true,
				sendSms: async text => {
					await REST.POST('sms/messages/send', {
						from: this.me,
						to: this.peer,
						text
					});
				},
				getMessagesContainer: () => this.messagesEl,
				isSendEnabled: () => !!this.me,
			}]
		}], this);

		this.reloadMessages(this.initialMessages);
		this.initialMessages = null;
	},

	async reloadMessages(msgs) {
		const messages = msgs || await this.loadMessages();
		this.messagesEl.gwReplaceChildren(messages.map(sms => ({
			className: 'thread-message' + ` thread-message-${sms.direction}`,
			children: [{
				className: 'contact-icon',
				children: [Utils.useIcon('user')]
			}, {
				className: 'thread-content',
				children: [{
					className: 'message-header',
					children: [{
						className: 'message-from',
						textContent: sms.me,
						nodeName: 'span',
					}, {
						className: 'message-sent-at',
						textContent: Formats.datetime(sms.sent_ts),
						nodeName: 'span',
					}],
				}, {
					className: 'message-text',
					textContent: sms.text,
				}],
			}],
		})));
		this.messagesEl.scrollTop = this.messagesEl.scrollHeight; // scroll to bottom, to the newest message
	},

	onBusMessage(msg) {
		if (msg.type == 'SMS_MESSAGE_SENT' || msg.type === 'SMS_MESSAGE_RECEIVED') {
			this.reloadMessages();
		}
	},
});

// }}}
// {{{ Office.SmsSendDialog

GW.define('Office.SmsSendDialog', 'Main.FormDialog', {

	title: _('Nová zpráva'),
	confirmText: _('Odeslat'),
	disableSubmitOnInvalidForm: true,
	
	accounts: env.SMS_ACCOUNTS,
	sender: null,
	to: null,

	renderFormFields() {
		return [{
			xtype: 'SelectField',
			name: 'from',
			label: _('Odeslat z'),
			options: this.accounts.map(a => ({
				value: a.number,
				text: a.name
			})),
			value: this.sender,
		}, {
			xtype: 'TextField',
			name: 'to',
			label: _('Příjemce'),
			value: this.to,

			enableSuggestions: true,
			createSuggestionPopup() {
				return new Office.LastContactSuggestionsPopup({
					searchContacts: true,
					lastContactsApi: 'contacts/last-sms',
				});
			},
			getViolation() {
				let val = this.getValue();
				val = String(val).trim().replace(/\s+/, '');

				if (!val.match(/^\+?[0-9*+#]{1,20}/)) {
					return _('Chybný formát čísla');
				}
			},
			'on:select': (c, v) => {
				c.setValue(v);
			},
		}, {
			xtype: 'FormField',
			name: 'text',
			label: _('Obsah zprávy'),
			renderInput()  {
				return {
					xtype: 'Office.SmsMessageInput',
					ref: 'smsTextInput',
					sendSms: null, // disable sending sms in input
					'on:change': ev => this.fire('change'),
				};
			},
			validate() {
				return !!this.getValue();
			},
			getValue() {
				return this.smsTextInput.refs.inputEl.value;
			}
		}];
	},

	async onSave(data) {
		await REST.POST('sms/messages/send', data);
	},
});

// }}}
// {{{ Office.SmsGate

GW.define('Office.SmsGate', 'GW.Component', {

	accounts: env.SMS_ACCOUNTS,
	selectedAccount: null,

	initComponent() {
		this.el = document.gwCreateElement({
			className: 'js-msgbus',
			socketSubscriptions: ['sms'],
			children: [{
				ref: 'tabs',
				xtype: 'Widgets.TabSwitcher',
				'on:beforeselect': (c, i) => {
					Application.route({
						paramsFn: params => {
							params.set('sms_tab', i.id);
							return params;
						},
					});
				},
			}, {
				ref: 'cards',
				xtype: 'Widgets.CardSwitcher'
			}],
		}, this);
	},

	showSendDialog(sender) {
		new Office.SmsSendDialog({
			parentElement: this.el,
			accounts: this.accounts,
			sender
		});
	},
	
	showThreadDialog(peer) {
		const loadMessages = async () => {
			const signal =  Utils.abortAndRetry(this, 'reload-thread-' + peer);
			const messages = await REST.GET('sms/messages', {peer}, {signal});
			return messages.sort((m1, m2) => m1.sent_ts - m2.sent_ts); // newest at the bottom
		}
		// load the messages before opening dialog 
		loadMessages().then(initialMessages => {
			new Office.SmsThreadDialog({
				parentElement: this.el,
				me: this.selectedAccount ? this.selectedAccount.number : null,
				peer,
				initialMessages,
				loadMessages
			});
		})
	},
	
	showReplyDialog(peer) {
		const me = this;
		new Main.FormDialog({
			parentElement: this.el,

			title: _('Odpověď'),
			confirmText: _('Odeslat'),
			disableSubmitOnInvalidForm: true,

			renderFormFields() {
				return [{
					xtype: 'FormField',
					name: 'text',
					label: _('Obsah zprávy'),
					renderInput() {
						return {
							xtype: 'Office.SmsMessageInput',
							ref: 'smsTextInput',
							sendSms: null, // disable sending sms in input
							'on:change': ev => this.fire('change'),
						};
					},
					validate() {
						return !!this.getValue();;
					},
					getValue() {
						return this.smsTextInput.refs.inputEl.value;
					}
				}];
			},

			async onSave(data) {
				await REST.POST('sms/messages/send', {
					from: me.selectedAccount.number,
					to: peer,
					text: data.text
				});
			},
		});
	},
	
	getTableColumns() {
		const me = this;
		return [{
			id: 'peer',
			name: _('Kontakt'),
			formatCell(td, v, row) {
				td.gwReplaceChildren([{
					nodeName: 'span',
					className: 'icon-wrapper',
					title: row.direction === 'received' ? _('Přijatá') : (row.sent_ts ? _('Odeslaná') : _("Zprávu se nepodařilo odeslat. Zkuste to prosím znovu")),
					children: [
						row.direction === 'received' ?
							Utils.useIcon('commentincomming', {cls: 'primary'})
							: (
								row.sent_ts ?
									Utils.useIcon('commentoutgoing', {cls: 'success'})
									: Utils.useIcon('warningtrianglefilled', {cls: 'error'})
							)
					],
				}, {
					className: 'contact',
					children: [{
						textContent: row.peer.number,
						className: 'contact-number text-nowrap',
					}, {
						':skip': !row.peer.name,
						textContent: row.peer.name,
						className: 'text-nowrap' + (row.peer.name ? ' text-sm' : ''),
					}]
				}]);
			},
		}, {
			id: 'sent_ts',
			name: _('Čas'),
			format: 'datetime_seconds'
		}, {
			id: 'text',
			name: _('Zpráva'),
			formatCell(td, v, row) {
				td.setAttribute("title", v);
				td.textContent = v;
			},
		}, {
			id: 'ctl',
			name: '',
			formatCell(td, v, row) {

				if (row.sent_ts) {
					td.gwCreateChild({
						nodeName: 'button',
						className: 'secondary small icon-left only-icon-mobile',
						children: [
							Utils.useIcon('reply'),
							{textContent: _("Odpověď")}
						],
						'on:click': ev => {
							ev.preventDefault();

							if (!row.read_ts) {
								REST.POST('sms/messages/' + row.id + '/read').then(() => {
									td.parentElement.classList.remove('not-read');
								});
							}
							
							me.showReplyDialog(row.peer.number);
						},
					});
				} else {
					td.gwCreateChild({
						nodeName: 'button',
						className: 'secondary small icon-left only-icon-mobile',
						children: [
							Utils.useIcon('reload'),
							{textContent: _("Opakovat")}
						],
						'on:click': async ev => {
							ev.preventDefault();
							await REST.POST('sms/messages/' + row.id + '/resend');
							me.reloadMessages();
						}
					});
				}

				td.gwCreateChild({
					nodeName: 'button',
					className: 'secondary small icon-only',
					title: _('Historie'),
					children: [Utils.useIcon('commenthistory')],
					'on:click': ev => {
						ev.preventDefault();

						if (!row.read_ts) {
							REST.POST('sms/messages/' + row.id + '/read').then(() => {
								td.parentElement.classList.remove('not-read');
							});
						}
						
						me.showThreadDialog(row.peer.number);
					},
				});
			},
		}];
	},

	getListComponentConfig(account) {
		let me = this;

		return {
			initComponent() {
				this.el = document.gwCreateElement({
					className: 'sms-container',
					children: [{
						className: 'header',
						children: [{
							':skip': account == null,
							nodeName: 'h4',
							className: 'title',
							textContent: account && account.name,
						}, {
							':skip': account != null, // show search field only for archive (no account selected)
							xtype: 'Widgets.DateRangeInput',
							ref: 'dateRange',
							'on:change': () => {
								Application.route({
									paramsFn: params => {
										let range = this.dateRange.getRange();
										if (range == 'custom') {
											let [since, until] = this.dateRange.getTimeRange();
											params.set('since', since || '');
											params.set('until', until || '');
											params.delete('range');
										} else {
											params.delete('since');
											params.delete('until');
											params.set('range', range);
										}
										return params;
									},
								});
							},
						}, {
							nodeName: 'button',
							className: 'primary small icon-left',
							children: [Utils.useIcon('plus'), {textContent: _('Nová zpráva')}],
							'on:click': () => {
								me.showSendDialog(account ? account.number : null);
							},
						}]
					}, {
						xtype: 'SmartTable',
						ref: 'table',
						monitorOverflow: true,
						getColumns() {
							return me.getTableColumns();
						},

						renderRow(tr, row) {
							tr.classList.toggle('not-sent', row.sent_ts == null);
							tr.classList.toggle('not-read', row.read_ts == null);
						}
					}]
				}, this);
			},
		};
	},

	onBusMessage(msg) {
		if (msg.type == 'SMS_MESSAGE_SENT' || msg.type === 'SMS_MESSAGE_RECEIVED') {
			this.reloadMessages();
		}
	},

	reloadMessages() {
		this.applyParams(new URLSearchParams(location.search));
	},

	async applyParams(params) {
		let signal = Utils.abortAndRetry(this, 'reload');

		/*
		 * Update toplevel tabs (queue list)
		 */
		let tabs = this.accounts.map(account => {
			return {
				id: 'account-' + account.number,
				title: account.name,
				number: account.number,
			};
		});

		tabs.push({
			id: 'archive',
			title: _('Archiv'),
		});

		this.tabs.setItems(tabs);

		/*
		 * Handle account selection.
		 */
		let tabId = params.get('sms_tab');
		let tab = tabs.find(t => t.id == tabId) || tabs[0];
		this.tabs.setSelected(tab.id);

		this.selectedAccount = this.accounts.find(a => a.number == tab.number);

		/*
		 * Render or activate the case list.
		 */
		let c = await this.cards.show({
			id: 'messages-' + (this.selectedAccount ? this.selectedAccount.number : 'archive'),
			...this.getListComponentConfig(this.selectedAccount),
		});
		
		const query = {
			me: this.selectedAccount ? this.selectedAccount.number : null,
		};

		if (c.dateRange) {
			let range = params.get('range') || 'today';
			await c.dateRange.setRangeOption(range);
			if (range == 'custom') {
				c.dateRange.setTimeRange(
					params.get('since'),
					params.get('until')
				);
			}
			const [since, until] = c.dateRange.getTimeRange();
			query.since = since;
			query.until = until;
		}

		c.table.setData(await REST.GET('sms/messages', query, {signal}));
	}
});

// }}}

// Speeddial:

// {{{ Office.Speeddial

GW.define('Office.Speeddial', 'GW.Component', {
	groups: [],
	selectedGroup: -1,
	settings: null,
	contacts: [],

	initComponent() {
		this.el = document.gwCreateElement({
			className: 'js-msgbus',
			children: [{
				className: 'toolbar',
				children: [{
					ref: 'tabs',
					xtype: 'Widgets.ButtonSwitcher',
					'on:beforeselect': (_, i) => {
						Application.route({
							paramsFn: params => {
								params.set('speeddial_group', i.id);
								return params;
							},
						});
					},
				}, {
					nodeName: 'button',
					ref: 'createGroupButton',
					title: _('Správa záložek'),
					type: 'button',
					className: 'secondary small',
					'on:click': () => this.openCreateContactGroupDialog(),
				}, {
					nodeName: 'button',
					ref: 'createContactButton',
					type: 'button',
					className: 'primary small icon-left',
					children: [Utils.useIcon('plus'), _('Přidat kontakt')],
					'on:click': () => this.openCreateContactDialog(),
				}],
			}, {
				ref: 'searchEl',
				nodeName: 'input',
				type: 'search',
				placeholder: _('Hledat jméno, firmu, linku'),
				className: 'small',
				'on:input': () => this.updateContactContainer(),
			}, {
				className: 'contact-container',
				ref: 'contactsContainer',
			}],
		}, this);

		this.createContactButton.gwHide(true);
		this.searchEl.gwHide(true);

		this.loadData();
	},

	'before:releaseComponent'() {
		Application.socketSend({type: 'SUBSCRIBE_EXTENSION_STATUSES', extensions: []});
	},

	openCreateContactDialog() {
		new Office.Speeddial.CreateContactDialog({
			onSave: data => this.createContact(data),
			selectedGroup: this.selectedGroup,
			groups: this.groups,
		});
	},

	openCreateContactGroupDialog() {
		new Office.Speeddial.CreateContactGroupDialog({
			onSave: data => this.onContactGroupsSave(data),
			groups: this.groups,
		});
	},

	async selectGroup(group_id) {
		const to_select = this.groups.find(g => g.id === group_id);
		if (to_select === undefined || to_select.id === this.selectedGroup.id) {
			return;
		}

		this.selectedGroup = to_select;
		this.tabs.setSelected(group_id);

		let signal = Utils.abortAndRetry(this, 'group-select');

		this.contacts = await REST.GET(`speeddial/groups/${group_id}/contacts`, null, {signal});
		await this.fetchContactStatuses(this.contacts, signal);

		let [ordered, rest] = Utils.orderByKeyList(this.contacts, this.selectedGroup.contact_positions);
		this.contacts = [...ordered, ...rest];

		this.registerForStatuses();
		this.updateContactContainer();
	},

	async onContactGroupsSave(data) {
		this.groups = await REST.POST('speeddial/groups/sync', 
			data.map(g => {
				return {
					id: g.id > 0 ? g.id : undefined,
					name: g.name,
				};
			},
		));

		this.settings.contact_group_positions = this.groups.map(g => g.id);
		await REST.PUT('speeddial/settings', this.settings);

		this.updateToolbar();
		if (this.groups.length === 0) {
			this.contacts = [];
			this.updateContactContainer();
		} else if (this.tabs.getSelected() == null) {
			Application.route({
				paramsFn: params => {
					params.set('speeddial_group', this.groups[0].id);
					return params;
				},
			});
		}
	},

	renderContactCards(contacts) {
		return contacts.map(c => this.renderCard(c));
	},

	renderCard(contact) {
		return {
			className: 'card',
			'attr:data-contact-id': contact.id,
			'attr:data-status': contact.status,
			children: [{
				className: 'avatar',
			}, {
				className: 'info',
				children: [{
					className: `phone`,
					textContent: contact.phone,
					title: contact.phone,
				}, {
					':skip': !contact.name && !contact.company,
					className: 'name',
					textContent: [contact.name, contact.company].filter(v => v).join(', '),
					title: [contact.name, contact.company].filter(v => v).join(', '),
				}, {
					':skip': !contact.description,
					className: 'description',
					textContent: contact.description,
					title: contact.description,
				}],
			}, {
				className: 'controls',
				children: [{
					nodeName: 'button',
					title: _('Vytočit'),
					type: 'button',
					'attr:data-action': 'dial',
					className: 'success icon-only small dialer-dial',
					children: [Utils.useIcon('phone-filled')],
					'on:click': () => Office.CallManager.doAction({action: 'dial', number: contact.phone}),
				},{
					nodeName: 'button',
					title: _('Přepojit'),
					type: 'button',
					'attr:data-action': 'blind-transfer',
					className: 'success-secondary icon-only small dialer-bl-transfer',
					children: [Utils.useIcon('arrow-right')],
					'on:click': () => Office.CallManager.doAction({action: 'blind-transfer', number: contact.phone}),
				},{
					nodeName: 'button',
					title: _('Přepojit s odložením'),
					type: 'button',
					'attr:data-action': 'attended-transfer',
					className: 'success-secondary icon-only small dialer-att-transfer',
					children: [Utils.useIcon('fi-corner-up-right')],
					'on:click': () => Office.CallManager.doAction({action: 'attended-transfer', number: contact.phone}),
				}, {
					nodeName: 'button',
					title: _('Více možností'),
					type: 'button',
					className: 'more transparent icon-only small',
					children: [Utils.useIcon('vdots')],
					'on:click': ev => {
						let popup = new PopupMenu({
							options: [{
								iconName: 'pencil',
								text: _('Upravit'),
								handle: () => this.editContact(contact),
							}, {
								iconName: 'repeat',
								text: _('Změnit pořadí'),
								handle: () => this.reorderContacts(),
							}, {
								iconName: 'delete',
								text: _('Odstranit'),
								handle: () => this.deleteContact(contact),
							}],
						});
						PopupManager.show(popup, {
							anchorEl: ev.target,
							forceAxis: 'vertical',
							preferReadingOrder: true,
						});
					},
				}]
			}, ]
		}
	},

	async createContact(contact) {
		const group = this.groups.find(g => g.id === contact.group_id);
		delete contact.group_id;

		const created_contact = await REST.POST(`speeddial/groups/${group.id}/contacts`, contact);

		group.contact_positions.push(created_contact.id);
		await REST.PUT(`speeddial/groups/${group.id}`, group);

		if (this.selectedGroup.id === group.id) {
			this.contacts.push(created_contact);

			await this.fetchContactStatuses([created_contact]);
			this.registerForStatuses();

			this.updateContactContainer();
		}
	},

	editContact(contact) {
		const contactEl = this.contactsContainer.querySelector(`[data-contact-id='${contact.id}']`);

		const onsave = async updated_contact => {
			updated_contact.id = contact.id;

			await REST.PUT(`speeddial/contacts/${contact.id}`, updated_contact);
			await this.fetchContactStatuses([updated_contact]);

			const idx = this.contacts.findIndex(c => c.id === updated_contact.id);

			// contact was moved to another group
			if (updated_contact.group_id !== this.selectedGroup.id) {
				this.contacts.splice(idx, 1);
				contactEl.remove();
				if (this.contacts.length === 0) {
					this.registerForStatuses();
					this.updateContactContainer();
				}

				// correct contact positions
				this.selectedGroup.contact_positions = this.contacts.map(c => c.id);
				await REST.PUT(`speeddial/groups/${this.selectedGroup.id}`, this.selectedGroup);

				const new_group = this.groups.find(g => g.id === updated_contact.group_id);
				new_group.contact_positions.push(updated_contact.id);
				await REST.PUT(`speeddial/groups/${new_group.id}`, new_group);

			// contact is staying in the same group
			} else {
				this.contacts[idx] = updated_contact;

				contactEl.gwReplaceChildren(this.renderCard(updated_contact).children);
				this.refreshControls();
			}
		};

		const me = this;
		const dialog = new Office.Speeddial.CreateContactDialog({
			title: _('Upravit kontakt'),
			enableSuggestions: false,
			onSave: onsave.bind(me),
			selectedGroup: this.selectedGroup,
			groups: this.groups,
		});

		dialog.prefillSuggestedContact(contact);
	},

	reorderContacts() {
		const onReorder = async reordered => {
			this.contacts = reordered;
			this.selectedGroup.contact_positions = reordered.map(c => c.id);
			await REST.PUT(`speeddial/groups/${this.selectedGroup.id}`, this.selectedGroup);
			this.updateContactContainer();
		};

		new Office.Speeddial.ContactOrderDialog({
			onSave: onReorder,
			items: this.contacts,
		})
	},

	async deleteContact(contact) {
		const contactEl = this.contactsContainer.querySelector(`[data-contact-id="${contact.id}"]`);

		await REST.DELETE(`speeddial/contacts/${contact.id}`);
		this.contacts.splice(this.contacts.findIndex(c => c.id === contact.id), 1);

		this.selectedGroup.contact_positions = this.contacts.map(c => c.id);
		await REST.PUT(`speeddial/groups/${this.selectedGroup.id}`, this.selectedGroup);

		contactEl.remove();
		if (this.contacts.length === 0) {
			this.updateContactContainer();
		}

		this.registerForStatuses();
	},

	async fetchContactStatuses(contacts, signal = null) {
		const statuses = await REST.POST('extensions/status/query', [...new Set(contacts.map(c => c.phone))], {signal});

		for (let contact of contacts) {
			contact.status = statuses[contact.phone];
		}
	},

	registerForStatuses() {
		Application.socketSend({
			type: 'SUBSCRIBE_EXTENSION_STATUSES',
			extensions: this.contacts.filter(c => c.status).map(c => c.phone),
		});
	},

	updateToolbar() {
		this.tabs.setItems(this.groups.map(g => {
			return {
				id: g.id, 
				title: g.name,
			};
		}));

		if (this.groups.length > 0) {
			this.createGroupButton.gwReplaceChildren([Utils.useIcon('pencil')]);
			this.createGroupButton.classList.remove('icon-left');
			this.createGroupButton.classList.add('icon-only');

			this.createContactButton.gwHide(false);
		} else {
			this.createGroupButton.gwReplaceChildren([Utils.useIcon('plus'), {
				textContent: _('Přidat záložku'),
			}]);
			this.createGroupButton.classList.remove('icon-only');
			this.createGroupButton.classList.add('icon-left');

			this.createContactButton.gwHide(true);
		}
	},

	updateContactContainer() {
		if (this.contacts && this.contacts.length > 0) {
			this.searchEl.gwHide(false);

			const filter = TextUtils.unaccent((this.searchEl.value || '').trim().toLowerCase());
			const filterKeywords = [...new Set(filter.split(' ').filter( v => v))];
			let filteredContacts = [...this.contacts];

			filterKeywords.forEach(keyword => {
				filteredContacts = filteredContacts.filter(c =>
					[c.phone, c.name, c.company, c.note].some(i => i && TextUtils.unaccent(i.toLowerCase()).includes(keyword))
				);
			});

			this.contactsContainer.gwReplaceChildren(this.renderContactCards(filteredContacts));
			this.contactsContainer.classList.remove('empty');
			this.refreshControls();

			if (!Application.isMobileUI()) {
				this.searchEl.focus();
			}
		} else {
			this.searchEl.gwHide(true);

			const has_groups = this.groups && this.groups.length > 0;

			this.contactsContainer.classList.add('empty');
			this.contactsContainer.gwReplaceChildren([{
				className: 'empty-image-placeholder',
				children: [{
					nodeName: 'img',
					src: APP_BASE + 'img/empty-boxes.svg',
					width: '234',
					height: '190',
				}, {
					className: 'title',
					textContent: has_groups ? _('Žádné kontakty') : _('V rychlých kontaktech je prázdno'),
				}, {
					':skip': has_groups,
					className: 'text',
					textContent: _('Nejprve si vytvořte záložky pro vaše kontakty.'),
				}, {
					nodeName: 'button',
					type: 'button',
					className: 'primary icon-left',
					children: [
						Utils.useIcon('plus'),
						has_groups ? _('Přidat kontakt') : _('Přidat záložku'),
					],
					'on:click': has_groups ? () => this.openCreateContactDialog() : () => this.openCreateContactGroupDialog(),
				}],
			}]);
		}
	},

	refreshControls() {
		for (let card of this.contactsContainer.querySelectorAll('.card')) {
			const avatarEl = card.firstChild;
			avatarEl.gwReplaceChildren([card.dataset.status === 'busy' || card.dataset.busy ? Utils.useIcon('phone-call') : (card.dataset.status ? Utils.useIcon('user') : Utils.useIcon('phone-mobile'))])
			for (let btn of card.querySelectorAll('button[data-action]')) {
				btn.disabled = (card.dataset.status && card.dataset.status != 'available') || card.dataset.busy;
				btn.gwHide(!Office.CallManager.canDo(btn.dataset.action));
			}
		}
	},

	updateContactCardStatus(contact) {
		const card = this.contactsContainer.querySelector(`[data-contact-id="${contact.id}"]`);
		if (card != null && contact.status != null) {
			card.dataset.status = contact.status;
		}
	},

	onBusMessage(msg) {
		switch (msg.type) {
		case 'CALL_MANAGER_CHANGE':
			// Update cards that I am in call with. Do not worry about the state of this.contacts,
			// which will get properly updated from the websocket.
			for (let card of this.contactsContainer.querySelectorAll("[data-busy]")) {
				delete card.dataset.busy;
			}

			const nums_in_calls = new Set(Office.CallManager.getState().calls.map(c => c.peer && c.peer.number).filter(v => v));
			const contacts_in_calls = this.contacts.filter(c => nums_in_calls.has(c.phone));

			for (let contact of contacts_in_calls) {
				const card = this.contactsContainer.querySelector(`[data-contact-id="${contact.id}"]`);
				if (card == null) {
					continue;
				}

				card.dataset.busy = true;
			}

			this.refreshControls();
			break;
		case 'EXTENSION_STATUS_CHANGE':
			this.fetchContactStatuses(this.contacts, Utils.abortAndRetry(this, 'fetch-on-exten-change')).then(() => {
				this.contacts.forEach(c => this.updateContactCardStatus(c));
				this.refreshControls();
			});
			break;
		case 'CLIENT_WEBSOCKET_STATUS_CHANGE':
			if (msg.ready) this.registerForStatuses();
			break;
		}
	},

	async loadData() {
		let signal = Utils.abortAndRetry(this, 'load-state');

		this.settings = await REST.GET('speeddial/settings', null, {signal});
		this.groups = await REST.GET('speeddial/groups', null, {signal});

		let [ordered, rest] = Utils.orderByKeyList(this.groups, this.settings.contact_group_positions, 'id');
		this.groups = [...ordered, ...rest];

		this.updateToolbar();

		if (this.groups.length == 0) {
			this.updateContactContainer();
		} else {
			const group_param = parseInt(new URLSearchParams(location.search).get('speeddial_group'));

			if (this.groups.some(g => g.id === group_param)) {
				this.selectGroup(group_param);
			} else {
				this.selectGroup(this.groups[0].id);
			}
		}
	},

	async applyParams(params) {
		const speeddial_group = parseInt(params.get('speeddial_group'));
		if (this.groups.some(g => g.id === speeddial_group)) {
			this.selectGroup(speeddial_group);
		}

		if (!Application.isMobileUI()) {
			this.searchEl.focus();
		}
	},
});

// }}}
// {{{ Office.Speeddial.CreateContactGroupDialog

GW.define('Office.Speeddial.CreateContactGroupDialog', 'Main.Dialog', {
	groups: [],

	tempGroupId: -1,

	title: _('Správa záložek'),

	'after:initComponent'() {
		this.bodyEl.gwReplaceChildren([{
			className: 'subtitle',
			textContent: _('Nová záložka'),
		}, {
			className: 'name-input-container',
			children: [{
				ref: 'groupNameInput',
				nodeName: 'input',
				type: 'text',
				placeholder: _('Např. Vedení, Obchod, Reklamace, Servis...'),
				$key_Enter: () => this.groupCreate(),
				'on:input': ev => this.createGroupButton.disabled = !ev.target.value,
			}, {
				nodeName: 'button',
				className: 'primary icon-left',
				ref: 'createGroupButton',
				type: 'button',
				disabled: true,
				children: [Utils.useIcon('plus'), {
					textContent: _('Vytvořit'),
				}],
				'on:click': () => this.groupCreate(),
			}],
		}, {
			ref: 'groupsContainer',
			className: 'contact-groups-container',
			children: [{
				className: 'subtitle',
				textContent: _('Aktivní záložky'),
			}, {
				className: 'contact-group-list',
				ref: 'groupList',
				children: [...this.groups.map(i => this.renderDraggableGroup(i))],
			}],
		}, {
			ref: 'saveButton',
			nodeName: 'button',
			type: 'button',
			className: 'primary save-button',
			textContent: _('Uložit'),
			'on:click': () => this.save(),
		}], this);

		this.groupsContainer.gwHide(this.groups.length == 0);
		this.saveButton.gwHide(this.groups.length == 0);

		this.dnd = DnDListSetup(this.groupList, {
			startFilterClass: 'group-item',
			itemClass: 'group-item',
			scrollCt: this.groupsContainer,
		});

		this.groupNameInput.focus();
	},

	'before:releaseComponent'() {
		this.dnd && this.dnd.destroy();
	},

	renderDraggableGroup(group) {
		return {
			className: 'group-item draggable-item',
			$groupId: group.id,
			children: [{
				className: 'drag-icon',
				children: [Utils.useIcon('dragindication')],
			}, {
				nodeName: 'input',
				type: 'text',
				value: group.name,
				'on:change': ev => {
					if (!ev.target.value) {
						ev.target.value = group.name; // set to original group name
					}
				}
			}, {
				nodeName: 'button',
				type: 'button',
				className: 'transparent icon-only',
				children: [Utils.useIcon('delete')],
				'on:click': ev => this.onGroupDelete(ev),
			}],
		}
	},

	groupCreate() {
		if (this.groupNameInput.value.trim().length === 0) {
			return;
		}

		const group = {
			id: this.tempGroupId--,
			name: this.groupNameInput.value,
		};

		this.groupList.gwCreateChild(this.renderDraggableGroup(group));

		this.setGroupListHidden(false);

		this.groupNameInput.value = '';
	},

	onGroupDelete(ev) {
		ev.currentTarget.parentElement.remove();

		this.setGroupListHidden(this.groupList.querySelectorAll('.group-item').length === 0);
	},

	setGroupListHidden(hidden) {
		this.groupsContainer.gwHide(hidden);

		// do not remove save button if there were groups
		// before we opened the dialog
		if (!hidden || this.groups.length == 0) {
			this.saveButton.gwHide(hidden);
		}
	},

	async save() {
		this.saveButton.disabled = true;

		try {
			await this.onSave(Array.from(this.groupList.querySelectorAll('.group-item')).map(el => {
				return {
					id: el.$groupId,
					name: el.querySelector('input').value.trim(),
				};
			}));
			// close dialog
			PopupManager.hideDownTo(this);
			PopupManager.hide(this);
		} catch (e) {
			this.saveButton.disabled = false;
			throw e;
		}
	},

	async onSave(changes) {
		console.log(changes);
	}
});
// }}}
// {{{ Office.InternalNumbers

GW.define('Office.InternalNumbers', 'GW.Object', {
	singleton: true,
	
	internalNumbers: null,
	
	async reload() {
		let signal = Utils.abortAndRetry(this, 'reload');
		this.internalNumbers = new Set(await REST.GET('extensions/numbers', null, {signal}));
		return this.internalNumbers;
	},

	async isInternal(exten) {
		return (await this.getAllInternals()).has(exten)
	},
	
	async getAllInternals() {
		return this.internalNumbers || await this.reload();
	}
	
});

// }}}
// {{{ Office.Speeddial.CreateContactDialog

GW.define('Office.Speeddial.CreateContactDialog', 'Main.FormDialog', {
	title: _('Nový kontakt'),

	confirmText: _('Uložit'),

	cancelText: _('Zrušit'),

	contactSuggestionsPopup: null,

	'after:initComponent'() {
		Office.InternalNumbers.reload().then(() => { // force reload internal numbers
			this.updateInternalMarker();
		});
	},

	renderFormFields() {
		return [{
			className: 'input-extension-container',
			children: [{
				xtype: 'TextField',
				label: _('Linka / Telefonní číslo'),
				name: 'phone',
				ref: 'phoneInput',
				enableSuggestions: true,
				createSuggestionPopup() {
					return new Office.SpeeddialSuggestionsPopup();
				},
				getViolation() {
					let val = this.getValue();
					val = String(val).trim().replace(/\s+/, '');
					
					if (!val.match(/^\+?[0-9*+#]{1,20}/)) {
						return _('Chybný formát čísla');
					}
				},
				'on:select': (c, v) => {
					c.setValue();
					if (v) {
						this.prefillSuggestedContact(v);
					}
				},
				'on:change': () => this.updateInternalMarker(),
			}, {
				ref: 'internalMarker',
				className: 'internal-marker',
				textContent: '',
			}],
		}, {
			xtype: 'TextField',
			label: _('Jméno'),
			name: 'name',
			optional: true,
		}, {
			xtype: 'TextField',
			label: _('Firma'),
			name: 'company',
			optional: true,
		}, {
			xtype: 'TextField',
			label: _('Popis'),
			name: 'description',
			optional: true,
		}, {
			xtype: 'SelectField',
			ref: 'groupSelect',
			label: _('Záložka'),
			name: 'group_id',
			options: this.groups.map(g => {
				return {
					text: g.name,
					value: g.id
				};
			}),
			value: this.selectedGroup.id,
		}];
	},

	async updateInternalMarker() {
		const value = this.phoneInput.getValue();
		if (!value) {
			delete this.internalMarker.dataset.kind;
		} else if (await Office.InternalNumbers.isInternal(value)){
			this.internalMarker.dataset.kind = 'internal';
			this.internalMarker.innerText = _('Interní linka');
		} else {
			this.internalMarker.dataset.kind = 'external';
			this.internalMarker.innerText = _('Externí linka');
		}
	},

	prefillSuggestedContact(contact) {
		this.setFormFieldsData({
			...contact,
			group_id: this.groupSelect.getValue(),
		});
		this.updateInternalMarker();
	},

	'before:destroy'() {
		if (this.contactSuggestionsPopup !== null) {
			PopupManager.hide(this.contactSuggestionsPopup);
		}
	}
});

// }}}
// {{{ Office.Speeddial.ContactOrderDialog

GW.define('Office.Speeddial.ContactOrderDialog', 'Main.Dialog', {
	title: _('Pořadí kontaktů'),

	items: [],

	'after:initComponent'() {
		this.bodyEl.gwReplaceChildren([{
			className: 'item-list',
			ref: 'itemList',
			children: [...this.items.map(i => this.renderDraggableItem(i))],
		}, {
			nodeName: 'button',
			className: 'save-button primary',
			textContent: _('Uložit'),
			'on:click': () => this.save(),
		}], this);
		this.dnd = DnDListSetup(this.itemList, {
			startFilterClass: 'draggable-item',
			itemClass: 'draggable-item',
			scrollCt: this.itemList,
		});
	},

	'before:releaseComponent'() {
		this.dnd && this.dnd.destroy();
	},

	renderDraggableItem(item) {
		return {
			className: 'draggable-item',
			$item: item,
			children: [{
				className: 'drag-icon',
				children: [Utils.useIcon('dragindication')],
			}, {
				textContent: this.renderTextContent(item),
			}],
		}
	},

	renderTextContent(item) {
		const name = [item.name, item.company, item.note].filter(i => i).join(', ');
		return `${item.phone}${name ? ' - ' + name : ''}`;
	},

	async save() {
		const reordered = Array.from(this.itemList.querySelectorAll('.draggable-item')).map(el => el.$item);

		PopupManager.hideDownTo(this);
		PopupManager.hide(this);

		await this.onSave(reordered);
	},

	async onSave(_reordered) {
	}
});

// }}}

// {{{ Office.SpeeddialSuggestionsPopup

GW.define('Office.SpeeddialSuggestionsPopup', 'SuggestionsPopup', {
	'after:initComponent'() {
		this.el.classList.add('dialer-input-suggestions');
		this.el.gwReplaceChildren([{
			className: 'title',
			textContent: _('Kontakty'),
		}, {
			ref: 'list',
		}], this);
	},

	renderItem(c) {
		return {
			$value: c,
			nodeName: 'a',
			href: '#',
			className: 'suggestion selectable',
			children: [{
				className: 'number',
				textContent: c.phone,
			}, {
				className: 'info',
				textContent: [c.name, c.company, c.description].filter(v => v).join(', '),
			}],
		};
	},

	async loadData(query) {
		query = query.trim();
		if (query == null || query.length === 0) {
			return;
		}

		const signal = Utils.abortAndRetry(this, 'suggestions');
		let suggestions = await REST.GET('contacts', {limit: 5, q: query}, {signal});

		const makeContactSuggestion = (phone, contact) => {
			return {
				name: contact.name,
				company: contact.company,
				description: contact.note,
				phone
			};
		};

		// split suggestions and determine which number matched
		const output = [];
		for (let contact of suggestions) {
			if (output.length > 5) break;

			let added = false;
			(contact.phones || []).forEach(phone => {
				if (phone.includes(query)) {
					added = true;
					output.push(makeContactSuggestion(phone, contact));
				}
			});

			if (!added) {
				output.push(makeContactSuggestion(contact.phones[0], contact));
			}
		}

		this.list.gwReplaceChildren(output.map(c => this.renderItem(c)));

		return output.length > 0;
	},
});

// }}}

// Right panel:

// {{{ Office.ControlPanel

GW.define('Office.ControlPanel', 'GW.Component', {
	initComponent() {
		this.el = document.gwCreateElement({
			className: 'panel-container',
			children: [{
				ref: 'tabs',
				xtype: 'Widgets.TabSwitcher',
				'on:beforeselect': (c, i) => {
					Application.route({
						paramsFn: params => {
							params.set('tab', i.id);
							return params;
						},
					});
				},
			}, {
				ref: 'cards',
				xtype: 'Widgets.CardSwitcher',
			}],
		}, this);

		this.tabs.setItems(this.getTabs());
	},

	getTabs() {
		return [];
	},

	async applyParams(params) {
		let tabs = this.tabs.getItems();
		let tabId = params.get('tab');
		let tab = tabs.find(t => t.id == tabId);
		if (!tab) {
			tab = tabs[0];
		}
		if (!tab) {
			// no tabs in UI
			return;
		}

		this.tabs.setSelected(tab.id);

		await this.cards.show({
			id: tab.id,
			...tab.card,
			params,
		});
	},
});

// }}}

// Main screen:

// {{{ Office.OfficePanel

GW.define('Office.OfficePanel', 'Main.Screen', {
	title: _('Office panel'),
	titleIcon: 'phone',
	enableExtenSelector: true,

	'after:initComponent'() {
		this.el.classList.add('js-msgbus');
		this.el.socketSubscriptions = ['sms'];

		this.bodyEl.gwReplaceChildren([{
			ref: 'dialer',
			...this.getDialerConfig(),
		}, {
			':skip': this.getControlPanelTabs().length == 0,
			ref: 'control',
			xtype: 'Office.ControlPanel',
			getTabs: () => this.getControlPanelTabs(),
		}], this);

		this.extendOfficePanel();

		this.layoutComponent();
		Office.CallManager.start();
	},

	getDialerConfig() {
		return {
			xtype: 'Office.Dialer',
		};
	},

	extendOfficePanel() {
	},

	getControlPanelTabs() {
		return [{
			id: 'speeddial',
			icon: 'dashboard',
			title: _('Rychlé volby'),
			href: '#',
			card: {
				xtype: 'Office.Speeddial',
			},
		}, {
			id: 'history',
			icon: 'phone',
			title: _('Historie volání'),
			href: '#',
			card: {
				xtype: 'Office.HistoryPanel',
			},
		}, {
			id: 'mailbox',
			icon: 'voicemail',
			title: _('Záznamník'),
			href: '#',
			card: {
				xtype: 'Office.Mailbox',
			},
		}, {
			id: 'directory',
			icon: 'book',
			title: _('Adresář'),
			href: '#',
			card: {
				xtype: 'Office.DirectoryPanel',
			},
		}];
	},

	'wrap:getProfileOptions'(fn) {
		let opts = fn();
                let idx = opts.findIndex(o => o.id == 'notifications');;
		if (idx >= 0) {
			idx++;
		}

		if (HTMLAudioElement.prototype.setSinkId) {
			opts.splice(idx, 0, {
				iconName: 'phone',
				text: _('Nastavení vyzváněcího zařízení'),
				handle: c => {
					new Office.RingerDeviceSetupDialog();
				},
			});
		}

		if (Office.Headsets.isSupported()) {
			opts.splice(idx, 0, {
				iconName: 'phone',
				text: _('Spravovat headsety'),
				handle: c => {
					new Office.HeadsetSetupDialog();
				},
			});
		}

		return opts;
	},

	'before:destroy'() {
		Office.CallManager.end();
	},

	getMovedCards() {
		if (Application.getResponsivityMatch('<md')) {
			return [{
				id: 'dialer',
				icon: 'phone-call',
				title: _('Dialer'),
				href: '#',
				card: this.getDialerConfig(),
			}];
		}

		return [];
	},

	layoutAutoHide() {
		const ltmd = Application.getResponsivityMatch('<md');
		this.dialer.el.gwHide(ltmd);
		if (!ltmd) {
			this.control.cards.remove('dialer');
		}
	},

	layoutComponent() {
		let ctl = this.control;

		if (ctl) {
			ctl.tabs.setItems(this.getMovedCards().concat(ctl.getTabs()));
			this.layoutAutoHide();

			ctl.applyParams(new URLSearchParams(location.search));
		}

		Application.dispatchMessage({
			type: 'CALL_MANAGER_CHANGE',
		});
	},

	showSMSDesktopNotification(msg) {
		Application.closeDesktopNotifications(c => c.role == 'received-sms');

		Application.desktopNotification({
			title: document.title,
			text: _('Příchozí SMS od {contact}')
				.replace('{contact}', msg.peer),
			//backgroundOnly: true,
			role: 'received-sms',
		});
	},

	onBusMessage(msg) {
		if (msg.type == 'RESPONSIVITY_CHANGE') {
			this.layoutComponent();
		}

		if (msg.type == 'CLIENT_WEBSOCKET_STATUS_CHANGE') {
			this.updateServerStatus();
		}

		if (msg.type === 'SMS_MESSAGE_RECEIVED') {
			this.showSMSDesktopNotification(msg);
		}

		// TODO reload mailboxes stats on some message
	},

	async reloadMailboxesStats() {
		if (this.control) {
			let signal = Utils.abortAndRetry(this, 'reload-mailboxes-stats');
			const summary = await REST.GET('mailboxes/summary', null, {signal});
			this.control.tabs.setNotification('mailbox', +summary.not_read_count);
		}
	},

	requestReload() {
		Utils.abortDedupeCalls(this, 'update-mailboxes-stats');
		this.reloadMailboxesStats();
	},

	applyParams(params) {
		this.updateServerStatus();
		this.requestReload();

		if (this.control) {
			return Promise.all([
				this.control.applyParams(params),
			]).then(() => {
				Application.refreshSubscriptionsFromDOM();
			});
		}
	},
});

// }}}

// Office panel routes:

Application.logoutActions.push(async function() {
	Office.CallManager.end();
});

Application.addRoutes([{
	group: 'auth',
	module: 'office',
	path: APP_BASE + 'office',
	async handler({params}) {
		let c = Application.show({
			id: 'office.main',
			xtype: 'Office.OfficePanel',
		});

		c.applyParams(params);
	}
}]);

Application.registerModule({
	id: 'office',
	title: _('Office panel'),
	tagline: _('Telefon a historie hovorů'),
	icon: 'phone',
	href: APP_BASE + 'office',
});
